/**
 * Der Zugang zur eigenen JSON-API. Die einzige Stelle, an der `fetch` steht.
 *
 * Die Ansichten bekommen ihn als Schnittstelle übergeben; die Tests der
 * Interaktionskerne setzen einen gefälschten Client ein (Spec, „Die Nähte").
 *
 * Ein unerwarteter Rumpf wird zum Fehler und nicht zu Ersatzwerten: Die Oberfläche
 * soll „nicht verbunden" zeigen und nichts erfinden.
 *
 * **Der Browser spricht nie mit dem SilverERP.** Jeder Weg dorthin führt über
 * diese API; die ERP-Sitzungskennung bleibt im Server, und was hier ankommt, ist
 * nur das, was eine Maske wirklich anzeigt.
 *
 * Die Pfade stehen hier ohne Unterpfad — `/api/status`, nicht
 * `/devtools/api/status`. Davorgesetzt wird er an der einen Stelle mit `fetch`
 * (`withBase`), damit die Anwendung auch hinter einem Proxy in einem Unterpfad
 * läuft, ohne dass 60 Aufrufe davon wissen müssen.
 */

import { withBase } from "../base";

/** Was `GET /api/status` über den Zustand des Gerüsts sagt. */
export interface ScaffoldStatus {
  application: string;
  /**
   * Die **Fassung** dieser Instanz — der Fassungsstempel aus dem Bau (ADR-0029).
   *
   * Leer ist kein Fehler, sondern der lokale Stand: Dann läuft kein Abbild, die
   * Kopfleiste sagt „Entwicklungsstand", und keine Fassung ist als laufend
   * gekennzeichnet. Sie ist nicht `schemaVersion` — das eine ist der Stand der
   * Datenbank, das andere der der Anwendung.
   */
  version: string;
  schemaVersion: number;
  journalMode: string;
  timestamp: string;
}

/**
 * Was der Browser über die Sitzung erfährt: der Anzeigename, sonst nichts.
 *
 * Keine Rolle und keine Rechte — eine Rolle in der Sitzung wäre eine Einladung
 * an die Oberfläche, aus ihr zu folgern, was jemand darf, und damit stünde
 * derselbe Rechteausdruck zweimal. Geprüft wird er im Server.
 */
export interface SessionState {
  displayName: string;
}

/** Die Rolle einer Person. Lokal geführt, nicht aus dem ERP. */
export type Role = "dev" | "management" | "admin";

export interface CurrentPerson {
  id: number;
  displayName: string;
  role: Role;
  /**
   * Ist dieser Person mindestens eine **SVN-Kennung** zugeordnet? (ADR-0028)
   *
   * Daran hängt allein, ob „meine Kennungen" und „an meinem Code" zu haben sind.
   * **Welche** Kennungen es sind, erfährt der Browser nicht — das ist die Kennung
   * an einer Gegenstelle, und hier steht ein Ja oder Nein.
   */
  hasSvnIdentity: boolean;
}

/**
 * Der Filter über den **Committer**: `me` und sonst nichts (ADR-0028).
 *
 * Wer gemeint ist, setzt der Server aus der Sitzung ein. Eine fremde Person kann
 * hier nicht stehen — „die Befunde an Müllers Code" ist eine Personalfrage und
 * keine Arbeitsfrage —, und der Server weist jeden anderen Wert ab.
 */
export type Committer = "me";

export interface Credentials {
  username: string;
  password: string;
}

/**
 * Ein Fehler der eigenen API. `code` ist das, woran der Client verzweigen darf —
 * am Text darf er es nicht.
 */
export class ApiError extends Error {
  public readonly code: string | null;

  public constructor(message: string, code: string | null = null) {
    super(message);
    this.name = "ApiError";
    this.code = code;
  }
}

/**
 * Der Kennung des einen Falls, in dem ein Handgriff zurückfragt: Ein **fremdes**
 * „behoben" würde überschrieben (ADR-0022).
 */
export const FOREIGN_FIX = "fixed_by_someone_else";

/**
 * Derselbe Fall als eigener Fehler — **mit Person und Zeitpunkt**.
 *
 * Sie stehen als eigene Felder daneben und nicht nur im Meldungstext: Die
 * Rückfrage nennt beides, und wer den Namen aus einem Satz herausschnitte,
 * bräche an dem Tag, an dem jemand den Satz umformuliert. Wie ein Zeitpunkt für
 * einen Menschen geschrieben wird, entscheidet die Oberfläche — hier steht er,
 * wie der Server ihn schickt.
 *
 * `fixedAt` darf `null` sein: Die Meldung trägt den Zeitpunkt, aber ein
 * fehlender ist kein Grund, die Rückfrage ausfallen zu lassen — dann steht der
 * Name allein da.
 */
export class ForeignFixError extends ApiError {
  public readonly fixedBy: string;
  public readonly fixedAt: string | null;

  public constructor(message: string, fixedBy: string, fixedAt: string | null) {
    super(message, FOREIGN_FIX);
    this.name = "ForeignFixError";
    this.fixedBy = fixedBy;
    this.fixedAt = fixedAt;
  }
}

export interface StatusApi {
  status(): Promise<ScaffoldStatus>;
}

/**
 * Ein **Eintrag** einer Fassung: ein Abschnitt ihres Textes — oder der Aufmacher
 * davor, der keine Überschrift hat (`heading === null`).
 *
 * `markdown` trägt die Ticketnummern **nicht**: Sie stehen daneben in `tickets`
 * und werden als abgesetzte Marken unter dem Eintrag gezeigt (ADR-0029). Im
 * Fließtext taucht damit nie eine Nummer auf.
 */
export interface VersionEntry {
  heading: string | null;
  markdown: string;
  tickets: number[];
}

/**
 * Eine ausgelieferte **Fassung** samt ihrem Text.
 *
 * `tickets` ist die Zahl **aller** Tickets der Fassung und nicht die der
 * erwähnten: Ein Eintrag nennt nur, was ein Nutzer künftig anders sieht, und die
 * Zahl im Kopf sagt, wie viel daneben noch drin steckte (ADR-0029).
 */
export interface Version {
  version: string;
  date: string;
  tickets: number;
  note: string | null;
  entries: VersionEntry[];
}

/**
 * Die **Versionshistorie**: die Fassungen dieser Anwendung, neueste oben.
 *
 * `running` ist die Fassung, die auf dieser Instanz läuft, oder `null` — dann
 * ist keine gekennzeichnet. `issueBaseAddress` ist die Adresse der Ticketliste,
 * an die eine Nummer kommt, oder `null`: Ohne eingestellte Instanz stehen die
 * Nummern ohne Verweis da.
 */
export interface VersionHistory {
  running: string | null;
  issueBaseAddress: string | null;
  versions: Version[];
}

export interface VersionsApi {
  versions(): Promise<VersionHistory>;
}

export interface SessionApi {
  signIn(credentials: Credentials): Promise<SessionState>;
  /** Das Nachsehen ist zugleich der Keepalive: Es schiebt den Ablauf hinaus. */
  session(): Promise<SessionState>;
  signOut(): Promise<void>;
  currentPerson(): Promise<CurrentPerson>;
}

/**
 * Der Nachweis, den der Gerätedialog bei der **Anmeldung** hergibt — in
 * Base64URL, so wie der Browser ihn liefert.
 *
 * Umgerechnet wird erst im Server: Dass die Gegenstelle hier Standard-Base64
 * will und beim Einrichten Base64URL, ist eine Eigenheit des SilverERP und
 * gehört an die Naht dorthin, nicht in den Browser.
 */
export interface PasskeyAssertion {
  credentialId: string;
  authenticatorData: string;
  clientData: string;
  signature: string;
  /** Nicht jeder Zugangsschlüssel führt eine Benutzerkennung mit sich. */
  userHandle: string | null;
  device: string;
}

/** Der Nachweis, den der Gerätedialog beim **Einrichten** hergibt. */
export interface PasskeyRegistration {
  credentialId: string;
  attestation: string;
  clientData: string;
  device: string;
  /** Die freiwillige Bezeichnung des Geräts. Erfunden wird keine. */
  label: string | null;
}

/**
 * Die vier Wege der **biometrischen Anmeldung** (#159) — zwei Zeremonien mit je
 * zwei Aufrufen.
 *
 * Die beiden Proben geben `unknown` zurück, und das ist Absicht: Es sind die
 * WebAuthn-Optionen der Gegenstelle, sie gehen unverändert an den Gerätedialog,
 * und ein Typ darüber wäre ein Vertrag über etwas, das wir nicht besitzen — und
 * einer, der bei jedem Feld falsch wird, das die Gegenstelle ergänzt.
 */
export interface PasskeyApi {
  passkeyChallenge(device: string): Promise<unknown>;
  signInWithPasskey(assertion: PasskeyAssertion): Promise<SessionState>;
  passkeySetupChallenge(device: string): Promise<unknown>;
  setUpPasskey(registration: PasskeyRegistration): Promise<void>;
}

/**
 * Der Bearbeitungsstand einer Revision. Gerechnet aus ihren Prüfaufträgen, nicht
 * gesetzt — `failed` heißt „unvollständig begutachtet".
 */
export type RevisionState = "pending" | "in_review" | "reviewed" | "failed";

/**
 * Die Übereinstimmung an der schwersten Stelle einer Revision: „n von m
 * Agenten", dazu die Zahl der verschiedenen aufgelösten Basismodelle.
 *
 * Das Basismodell steht daneben, weil Übereinstimmung Übereinstimmung misst und
 * nicht Richtigkeit: Zwei Agenten auf demselben Basismodell irren gleich und
 * bestätigen einander. Kontrollbefunde gehen in `carrying` nicht ein (ADR-0010)
 * — ein Kontrollmuster ist keine Meinung.
 */
export interface Agreement {
  /** Wie viele Agenten die Stelle tragen — das „n". */
  carrying: number;
  /** Wie viele Agenten die Revision begutachtet haben — das „m". */
  reviewing: number;
  baseModels: number;
}

/**
 * Das Wärmebild einer Zeile: wie viel in der Revision liegt und wie schwer es
 * wiegt, ohne dass jemand sie öffnet.
 *
 * Zahl, Schwere und Übereinstimmung stehen nebeneinander und werden nicht zu
 * einer Punktzahl verrechnet. `severity` und `agreement` sind `null`, solange es
 * nichts zu melden gibt; die Zahl steht dann auf 0.
 */
export interface RevisionHeat {
  total: number;
  severity: Severity | null;
  /**
   * Der Titel des relevantesten Befundes — der der schwersten Stelle, und dort
   * der des schwersten Befundes.
   *
   * Leer heißt: Die Befunde dort tragen keinen Titel; die Zeile zeigt dann Zahl
   * und Schwere allein. Eine leere Zeichenkette und nicht `null`, weil „ohne
   * Titel" kein eigener Zustand ist, sondern ein Befund aus einem Lauf vor der
   * Titelpflicht.
   */
  title: string;
  /**
   * Die Urheber der schwersten Stelle — dieselbe Stelle, von der auch der Titel
   * kommt.
   *
   * Namen und nicht nur Zahlen: „2 von 2 Agenten" sagt, wie viele etwas gesehen
   * haben, aber nicht wer. Ein Kontrollmuster kommt als `pattern` mit; die
   * Ansicht schreibt dafür „Regel".
   */
  authors: SiteAuthor[];
  agreement: Agreement | null;
}

/**
 * Ein **Ticketverweis**, wie eine Listenzeile ihn trägt (#113): der im
 * Commit-Kommentar erkennbare Zeiger auf einen Vorgang im Ticketsystem.
 *
 * `label` ist das **Kürzel der Ticketliste samt Nummer** — `DEV-435` (#131) —,
 * `ticketList` die Liste `owner/repo`, in der der Vorgang liegt. Beides gehört
 * zusammen: Zweimal „435" aus zwei Listen sind zwei verschiedene Vorgänge.
 *
 * **Es ist nicht der Text aus dem Kommentar.** Derselbe Vorgang wird als
 * `Dev435`, `yQos/dev-liste#435` und als volle Adresse genannt; in einer Liste
 * nebeneinander wären das drei Schreibweisen für eine Sache. Ist für die Liste
 * kein Kürzel eingetragen, steht hier `owner/repo#nummer`. Der Kommentar selbst
 * bleibt unverändert — dort liegt der Link weiter auf dem getippten Wortlaut.
 *
 * **Erkannt hat der Server**, und zwar beim Ausliefern. Ob es den Vorgang gibt,
 * bleibt dabei ungeprüft: Eine Liste mit dreißig Zeilen löste sonst dreißig
 * Anfragen an eine fremde Gegenstelle aus (ADR-0027).
 */
export interface IssueReference {
  label: string;
  ticketList: string;
  number: number;
  address: string;
}

/**
 * Eine Zeile des Revisionsstroms.
 *
 * `personName` ist der Anzeigename hinter der SVN-Kennung, sofern ein
 * Administrator sie zugeordnet hat; `null` heißt, die Kennung gehört noch
 * niemandem. `reviewRecommended` heißt: mindestens ein Agent hat diese Revision
 * markiert. `submitted` ist die Abgabe **der angemeldeten Person**.
 */
export interface RevisionSummary {
  id: number;
  projectId: number;
  projectName: string;
  number: number;
  svnIdentity: string;
  personName: string | null;
  committedAt: string;
  message: string;
  state: RevisionState;
  reviewRecommended: boolean;
  submitted: boolean;
  /**
   * Arbeitet die angemeldete Person in dieser Revision blind — über ihre
   * Eigenschaft `blind_review` oder über ihre Blindabsicht (ADR-0013)?
   */
  blind: boolean;
  /**
   * Steht für **diese** Revision ihre Blindabsicht? Nur dann gibt es etwas
   * zurückzunehmen: `blind` daneben ist die Oder-Verknüpfung aus Eigenschaft und
   * Absicht, und wer `blind_review` trägt, hat nie einen Knopf gedrückt.
   */
  blindRequested: boolean;
  /**
   * Steht ihr die Blindabsicht für diese Revision noch offen? `false`, sobald
   * ihr fremde Befunde ausgeliefert wurden: Blind geht nur, solange nichts
   * Fremdes ausgeliefert wurde.
   */
  blindPossible: boolean;
  /**
   * Wann zu dieser Revision zuletzt **zugestellt** wurde — `null`, wenn noch
   * nie (ADR-0027).
   *
   * Sie ist ausdrücklich **kein Nachhalten** (ADR-0012) und kein
   * Bearbeitungsstand: Sie sagt „darüber wurde geredet", nicht „erledigt". Ein
   * Fehlschlag steht im Bestand und setzt sie nicht — was die Gegenstelle nie
   * erreicht hat, ist keine Zustellung.
   */
  deliveredAt: string | null;
  /**
   * Die **Ticketverweise** aus dem Commit-Kommentar dieser Revision (#113) —
   * alle, in der Reihenfolge ihres Vorkommens. Leer heißt: Der Kommentar nennt
   * keinen.
   *
   * Wie viele davon in die Spalte passen, entscheidet die Ansicht; der Server
   * kürzt eine Menge nicht, deren Anzeige er nicht kennt.
   */
  issueReferences: IssueReference[];
  /**
   * Das Wärmebild dieser Zeile — `null`, wenn es nicht ausgeliefert wird.
   *
   * Das ist der Fall, solange die angemeldete Person diese Revision blind
   * durchgeht: Auch „drei Befunde, einer schwer" lenkt eine Durchsicht. `null`
   * heißt deshalb „nicht zu sehen" und nicht „nichts gefunden" — dafür steht die
   * Zahl 0 im Wärmebild selbst.
   */
  findings: RevisionHeat | null;
}

/** Ein Projekt, wie der Projektfilter des Stroms es anbietet. */
export interface ProjectOption {
  id: number;
  name: string;
}

/**
 * Was der Strom auf eine Anfrage liefert: die Seite, ihre Einordnung und die
 * Werte, nach denen sich filtern lässt.
 *
 * Alles in einer Antwort, weil der Strom im Sekundentakt gefragt wird (ADR-0008)
 * und drei Anfragen je Takt dreimal so viele Apache-Prozesse für dasselbe Bild
 * wären.
 */
export interface RevisionStream {
  revisions: RevisionSummary[];
  page: number;
  perPage: number;
  pages: number;
  total: number;
  projects: ProjectOption[];
  svnIdentities: string[];
}

/**
 * Die Frage an den Strom. `null` heißt bei jedem Filter „danach wird nicht
 * gefiltert" — `false` heißt ausdrücklich „ohne".
 */
export interface RevisionStreamQuery {
  page: number;
  perPage: number;
  projectId: number | null;
  svnIdentity: string | null;
  reviewRecommended: boolean | null;
  state: RevisionState | null;
  submitted: boolean | null;
  /** Revisionen, in denen ein Befund **dieses** Grades liegt — keine Untergrenze. */
  severity: Severity | null;
  hasFindings: boolean | null;
  /**
   * Nur die eigenen Commits — über **alle** SVN-Kennungen der Person (ADR-0028).
   *
   * Derselbe Filter wie `svnIdentity`, nur über die Person gesprochen: Weil eine
   * Person mehrere Kennungen hat, kann eine einzelne Kennung das nicht sagen.
   */
  committer: Committer | null;
}

/**
 * Eine Zeile des **Befundvorrats**: eine **Stelle**, quer über alle Revisionen.
 *
 * Sie fasst alle Befunde zusammen, die einander an einem Pfad in einer Revision
 * überlappen — drei Agenten an derselben Stelle ergeben **eine** Zeile und nicht
 * drei. Sie wird beim Fragen gerechnet und liegt nirgends abgelegt.
 *
 * `revisionId` ist der Weg in die Revisionsansicht; `null` heißt, dass keine
 * Revision diese Zeilen zeigt, und dann führt kein Weg hinein statt in eine
 * geratene Revision.
 */
/**
 * Ein **Urheber** an einer Stelle: sein Bezeichner und woher er kommt.
 *
 * Die Herkunft steht daneben, weil der Bezeichner allein nicht reicht: „Zuweisung
 * in einer Bedingung" ist die Beschriftung eines Kontrollmusters und läse sich
 * unter Agentennamen wie ein weiterer Agent. Die Ansicht schreibt für ein Muster
 * deshalb „Regel" und hält die Beschriftung im Titel bereit.
 */
export interface SiteAuthor {
  name: string;
  origin: FindingOrigin;
}

export interface Site {
  projectId: number;
  projectName: string;
  path: string;
  /**
   * Derselbe Pfad ohne den überwachten Unterbaum des Projekts — **nur für die
   * Anzeige**. Der Anker ist und bleibt `path`: Er identifiziert die Stelle und
   * geht in Sprung und Übergabe ein.
   *
   * Gleich `path`, wo der Pfad nicht unter dem Unterbaum liegt — der Server
   * kürzt nur, was nachweislich davorsteht.
   */
  relativePath: string;
  /** Die Revision des **Ankers** (ADR-0006) — „Datei @ Revision". */
  revisionNumber: number;
  revisionId: number | null;
  /**
   * Der **Entwickler**, der die Revision übergeben hat (#129): die SVN-Kennung,
   * unter der eingecheckt wurde, und der Anzeigename dahinter, sofern ein
   * Administrator die Kennung zugeordnet hat.
   *
   * Dieselben zwei Felder wie am Revisionsstrom und aus demselben Grund derselbe
   * Name: Es ist dieselbe Auskunft (ADR-0009). `personName` gleich `null` heißt,
   * die Kennung gehört noch niemandem — zurückgehalten wird die Zeile dafür
   * nicht.
   *
   * Die **leere Kennung** heißt: Es gibt keine Revision und damit keinen Commit.
   * Die Spalte bleibt dann leer, denn eine Stelle aus einem Bestandslauf trägt
   * keinen Committer, und eine erfundene Zuschreibung wäre hier der teuerste
   * Fehler.
   */
  svnIdentity: string;
  personName: string | null;
  /**
   * Wann diese Stelle zuletzt **zugestellt** wurde — `null`, wenn noch nie
   * (ADR-0027). Dieselbe Marke wie am Revisionsstrom und aus demselben Grund
   * kein Nachhalten: Sie sagt „darüber wurde geredet".
   */
  deliveredAt: string | null;
  startLine: number;
  endLine: number;
  /** Die höchste an dieser Stelle gemeldete Schwere — nach ihr wird sortiert. */
  severity: Severity;
  /**
   * Die niedrigste. Gleich der höchsten heißt: Alle Befunde hier nehmen die
   * Stelle gleich schwer. Verschieden heißt: Sie tun es nicht, und das ist eine
   * Auskunft über die Stelle und kein Widerspruch, den die Ansicht auflöst.
   */
  lowestSeverity: Severity;
  /**
   * Der **Titel** der Stelle: der des schwersten Befundes darunter. Leer heißt,
   * dass keiner der Befunde hier einen trägt — sie stammen aus einem Lauf vor
   * der Titelpflicht. Die Ansicht erfindet dann keinen.
   */
  title: string;
  /** Die Zahl der Befunde an dieser Stelle. */
  total: number;
  authors: SiteAuthor[];
  /** Der Lebenszyklus, wie er hier vorkommt — unter „nur offen" genau ein Wert. */
  statuses: FindingStatus[];
  /**
   * Die **Qualifizierungen**, die hier vorkommen — die zweite Achse, neben den
   * Status und nicht in ihnen. Leer heißt: Hier hat noch niemand geurteilt.
   */
  qualifications: Qualification[];
  /** Wann hier zuletzt qualifiziert wurde. `null`, solange niemand geurteilt hat. */
  qualifiedAt: string | null;
  /** **Wer** zuletzt qualifiziert hat. Leer heißt: Es gibt kein Urteil. */
  qualifiedBy: string;
  /**
   * Die Befundarten, die hier vorkommen — **alle**. Zwei Agenten dürfen
   * dieselbe Stelle verschieden einordnen; welche Einordnung „gilt",
   * entscheidet niemand.
   */
  kinds: FindingKind[];
  /** `null`, wenn kein Agent die Stelle trägt: „0 von 0" wäre keine Auskunft. */
  agreement: Agreement | null;
  /**
   * Die **Ticketverweise** aus dem Commit-Kommentar der Revision, in die der
   * Sprung führt (#113). Leer heißt: Der Kommentar nennt keinen — oder es gibt
   * keine Revision und damit keinen Kommentar.
   */
  issueReferences: IssueReference[];
  /**
   * Der **Commit-Kommentar** derselben Revision — dieselbe Auskunft wie im
   * Revisionsstrom und unter demselben Feldnamen.
   *
   * Leer heißt: Es gibt keine Revision und damit keinen Commit. Wer im Vorrat
   * eine Stelle liest, entscheidet, ob er sie anfasst, und die Absicht des
   * Commits gehört zu dieser Entscheidung; der Verweis allein trägt sie nur,
   * wenn es einen Vorgang gibt.
   */
  message: string;
}

/**
 * Was der Vorrat auf eine Anfrage liefert: die Seite, ihre Einordnung, die
 * Projekte des Filters — und die Zahl, mit der er sagt, was ihm fehlt.
 *
 * `incompleteRevisions` zählt die Revisionen des gefilterten Ausschnitts, deren
 * Begutachtung endgültig liegengeblieben ist. Ohne sie wäre eine leere Liste
 * doppeldeutig: „nichts gefunden" oder „nie angesehen".
 *
 * `belowThreshold` zählt die Stellen, die die **Anzeigeschwelle** ihres Projekts
 * weggenommen hat. Ohne diese Zahl sähe eine gekürzte Liste aus wie eine, in der
 * wirklich nichts mehr offen ist — und eine Schwelle, von der niemand erfährt,
 * wäre ein Löschwerkzeug mit freundlichem Namen. In `total` geht sie nicht ein.
 */
export interface FindingSites {
  sites: Site[];
  page: number;
  perPage: number;
  pages: number;
  total: number;
  projects: ProjectOption[];
  incompleteRevisions: number;
  belowThreshold: number;
}

/**
 * Die Frage an den Vorrat. `null` heißt bei jedem Filter „danach wird nicht
 * gefiltert".
 *
 * `from` und `to` sind Tage als `YYYY-MM-TT` und meinen den Zeitpunkt der
 * **Revision** — „was ist aus diesen Tagen offen". Beide zählen mit.
 */
export interface SiteQuery {
  page: number;
  perPage: number;
  projectId: number | null;
  /** Ein Ausschnitt des Pfades, nicht der ganze. */
  path: string | null;
  kind: FindingKind | null;
  severity: Severity | null;
  /** Der **Lebenszyklus** — „was ist hier noch zu tun". */
  status: FindingStatus | null;
  /**
   * Die **Qualifizierung** — „hatte die Maschine recht". Ein eigener Filter.
   *
   * Dazu `"unjudged"` für die Stelle, an der noch niemand geurteilt hat (#185);
   * `null` heißt unverändert „alle".
   */
  qualification: QualificationFilter | null;
  /** Agent, Kontrolle oder Person. */
  origin: FindingOrigin | null;
  from: string | null;
  to: string | null;
  /**
   * Ob die **Anzeigeschwellen** der Projekte wirken sollen. Anders als die
   * übrigen Filter geht dieser immer mit: Ohne die Angabe wirkt im Server keine
   * Schwelle, und `false` ist hier eine Aussage und kein fehlender Wert.
   */
  thresholds: boolean;
  /**
   * Nur die Befunde **an meinem Code**: die, deren Ankerrevision die fragende
   * Person übergeben hat (ADR-0028). Bestandsbefunde tragen keinen Committer und
   * fallen dabei heraus.
   */
  committer: Committer | null;
}

/**
 * Der **Anker einer Stelle**: Projekt, Pfad, Revision, Zeilenbereich (ADR-0006).
 *
 * Die einzige Kennung, die eine Stelle hat — sie wird bei der Abfrage gerechnet
 * und liegt nirgends abgelegt (ADR-0015). Jede Zeile des Vorrats und jeder
 * Befund tragen diese fünf Angaben und passen deshalb hier hinein, ohne dass
 * eine Ansicht etwas umbaut.
 */
export interface SiteAnchor {
  projectId: number;
  path: string;
  revisionNumber: number;
  startLine: number;
  endLine: number;
}

/**
 * Der **Handgriff an einer Stelle** (ADR-0022; Spec v1.4, „Die vier
 * Handgriffe").
 *
 * Ein Wert je Handgriff und nicht zwei Felder, die der Aufrufer frei kombiniert:
 * Welche Kombinationen der beiden Achsen es gibt, ist eine Festlegung der Spec.
 * Welche Achsen er setzt, entscheidet der Server:
 *
 * | Handgriff | Qualifizierung | Lebenszyklus |
 * | --- | --- | --- |
 * | `false-alarm` | `rejected` | `dismissed` |
 * | `not-pursued` | `acknowledged` | `dismissed` |
 * | `to-fix` | `confirmed` | `open` |
 * | `fixed` | `confirmed` | `fixed` |
 *
 * Die Werte bleiben englisch wie jede geschlossene Menge, die das Glossar nicht
 * selbst festlegt (ADR-0009); ihre Beschriftungen stehen in `views/findings.ts`.
 * Die **Rücknahme** ist kein fünfter Wert, sondern ein eigener Weg — einen
 * „Handgriff" leer zu nennen hieße, ihn qualifizieren zu können.
 */
export type Verdict = "false-alarm" | "not-pursued" | "to-fix" | "fixed";

/**
 * Der Handgriff und seine Rücknahme, beide **an der Stelle**.
 *
 * Sie stehen hier und nicht am Vorrat, weil bewertet wird, wo der Code steht:
 * Die Liste sagt, wie es um eine Stelle steht, und setzt nichts (ADR-0022).
 * Zurück kommt die Zahl der Befunde, an denen der Handgriff gewirkt hat — die Fläche
 * lädt danach ohnehin neu.
 */
export interface SiteVerdictApi {
  /**
   * Die Stelle bewerten. `force` schreibt über ein **fremdes** „behoben"; ohne
   * ihn weist der Server diesen einen Fall mit `ForeignFixError` ab, damit die
   * Fläche einmal zurückfragen kann.
   */
  setSiteVerdict(anchor: SiteAnchor, verdict: Verdict, force: boolean): Promise<number>;
  /**
   * Die Rücknahme: leere Qualifizierung, `open`, an allen Befunden der Stelle.
   * Sie steht jedem offen, der die Stelle sieht (ADR-0022).
   */
  withdrawSiteVerdict(anchor: SiteAnchor): Promise<number>;
}

/**
 * Der **Zuschnitt** einer Zustellung: die ganze Revision oder eine Stelle darin
 * (ADR-0027).
 *
 * Beide sind Zuschnitte derselben **Übergabe**, und welcher der richtige ist,
 * entscheidet der Anlass. Die Revision steht in beiden Fällen da: An ihr hängt
 * die Abgabe, und ohne Abgabe wird nicht zugestellt.
 */
export interface DeliveryCut {
  revisionId: number;
  /** Der Anker der Stelle — `null` heißt: die ganze Revision. */
  anchor: SiteAnchor | null;
}

/**
 * Ein **Ziel** einer Zustellung: ein Vorgang im Ticketsystem, den es gibt.
 *
 * Titel und Zustand stehen dabei, weil niemand blind in ein fremdes Ticket
 * schreiben soll — eine Nummer allein sagt nicht, wo der Text landet. `open`
 * verbietet nichts: Ein Kommentar an einem abgeschlossenen Vorgang ist erlaubt
 * und oft genau die Information, die dort fehlt.
 */
export interface DeliveryTarget {
  owner: string;
  repo: string;
  number: number;
  /** Das Ziel in lesbarer Form — `owner/repo#4711`. */
  target: string;
  title: string;
  open: boolean;
  address: string;
}

/**
 * Ein **Empfänger** einer Direktnachricht (#104): eine Person dieses Systems, die
 * eine Mattermost-Kennung trägt.
 *
 * **Die Kennung steht nicht dabei**, und das ist Absicht (ADR-0027): Sie ist die
 * Kennung an einer zweiten Gegenstelle und steht in der Oberfläche nur im
 * Verwaltungsdialog. Gewählt wird über die **Person**; wohin der Text geht, sagt
 * der Anzeigename, und die Kennung liest der Server.
 */
export interface ChatRecipient {
  personId: number;
  displayName: string;
}

/**
 * Der **Chatteil** der Auskunft: an wen eine Direktnachricht gehen kann.
 *
 * `suggested` ist der Urheber der Revision, aufgelöst SVN-Kennung → Person →
 * Mattermost-Kennung — `null` heißt: keine Vorbelegung, und `obstacle` sagt
 * warum. Vorbelegt und nicht festgelegt: Der Autor kann im Urlaub sein, das Team
 * gewechselt haben oder der Commit ein Sammelcommit sein, und ein fester
 * Empfänger baute die erste Umgehung am Tag eins ein.
 *
 * Eine leere Liste heißt: Diesen Weg gibt es hier nicht — kein Zugang hinterlegt
 * oder niemand trägt eine Kennung. Auch dann steht der Grund im Klartext da.
 */
export interface DeliveryChat {
  recipients: ChatRecipient[];
  suggested: number | null;
  obstacle: string | null;
}

/**
 * Was der Zustelldialog beim **Öffnen** erfährt — und nur dann: Beim Anzeigen
 * einer Liste ergeht keine Anfrage ans Ticketsystem (#101).
 *
 * `obstacle` sagt, dass es **kein Ziel** gibt, und steht im Klartext da — ein
 * Knopf, der ohne Erklärung fehlt oder grau ist, erzeugt genau die Rückfrage,
 * die er ersparen soll.
 *
 * Beide Wege stehen in **einer** Antwort (#104): Sie werden zusammen gelesen, und
 * zwei Anfragen wären zwei Gelegenheiten, dass der Dialog halb beantwortet
 * dasteht. Der Chat wird dabei überhaupt nicht gefragt — der Empfänger kommt aus
 * dem eigenen Bestand.
 */
export interface DeliveryPreparation {
  targets: DeliveryTarget[];
  obstacle: string | null;
  /** Wann dieser Zuschnitt zuletzt draußen war — `null`, wenn noch nie. */
  deliveredAt: string | null;
  chat: DeliveryChat;
}

/** Was zurückkommt, wenn der Text draußen ist: wohin, wann, und was genau. */
export interface Delivered {
  target: string;
  at: string;
  text: string;
}

/**
 * Die **Zustellung** (ADR-0027): die zwei Wege, auf denen der Übergabetext das
 * Haus verlässt — als Kommentar an einem bestehenden Vorgang (#102) und als
 * Direktnachricht an einen Menschen (#104).
 *
 * **Je Weg ein eigener Aufruf.** Zwei Aufrufe und nicht einer mit einem Schalter: Ein
 * Mensch drückt einen Knopf, der genau einen Weg bedient, und der Aufruf daneben
 * soll genauso aussehen.
 *
 * **Der Rumpf wird nicht mitgeschickt.** Er entsteht im Server aus dem
 * Befundstand; hier geht nur das freie Vorwort hinaus. Wer den Rumpf umschreiben
 * könnte, stellte etwas zu, das dort nie so stand — und zurückzuholen ist es
 * nicht.
 */
export interface DeliveryApi {
  deliveryPreparation(cut: DeliveryCut): Promise<DeliveryPreparation>;
  deliver(cut: DeliveryCut, target: DeliveryTarget, preface: string): Promise<Delivered>;
  /**
   * Die **Direktnachricht** an eine Person.
   *
   * Hinaus geht ihre Kennung im Datensatz und nie ihre Mattermost-Kennung: Welche
   * daran hängt, liest der Server — sonst wäre dies ein Weg, an einen beliebigen
   * Menschen zu schreiben.
   */
  deliverToChat(
    cut: DeliveryCut,
    recipient: ChatRecipient,
    preface: string,
  ): Promise<Delivered>;
}

export interface FindingSitesApi extends DeliveryApi {
  findingSites(query: SiteQuery): Promise<FindingSites>;
  /**
   * Die **Übergabe** zu einer Stelle: der kopierfertige Text, mit dem ein Mensch
   * die Behebung anderswo anstößt (Glossar, „Übergabe").
   *
   * Zusammengestellt wird er im Server — nur dort liegen die Befundtexte und der
   * abgelegte Diff-Ausschnitt. Angesprochen wird die Stelle über ihren **Anker**:
   * Eine Kennung hat sie nicht, denn sie wird bei der Abfrage gerechnet.
   */
  handoff(site: Site): Promise<string>;
}

/**
 * Eine Revision, wie die Revisionsansicht sie beim Öffnen bekommt. Ohne
 * Projektnamen und ohne Person: Beides steht im Strom, aus dem man kommt.
 */
export interface Revision {
  id: number;
  projectId: number;
  number: number;
  svnIdentity: string;
  committedAt: string;
  message: string;
  state: RevisionState;
}

/** Was mit einer Datei in einer Revision geschehen ist. */
export type ChangeAction = "added" | "modified" | "deleted" | "moved";

/**
 * Ein Eintrag der Dateiliste.
 *
 * `previousRevision` ist die Revision, in der diese Datei zuletzt vor der
 * begutachteten geändert wurde — **nicht** die um eins kleinere Nummer und
 * ausdrücklich **kein Anker**: Ein Befund nennt immer die begutachtete Revision
 * (ADR-0019). `null` heißt: Die Datei wurde in dieser Revision angelegt, es gibt
 * keine.
 *
 * `skippedReason` ist die Marke des **Pollers** und gilt für alle Agenten:
 * binär, über der Größengrenze, oder nach der Wandlung kein gültiges UTF-8. Dort
 * sind Diff und Inhalt leer, und es gibt nichts anzusehen.
 *
 * `agentSkips` sind die Übersprünge **einzelner Agenten**: Diff und vollständige
 * Datei passten nicht in ihre Kontextgrenze, die je Agent steht (ADR-0032).
 * Geleert wird dabei nichts — die Datei ist vollständig da und wird angezeigt,
 * der Grund steht daneben. Leer ist die Liste `[]`; das heißt „kein Agent hat
 * übersprungen".
 */
export interface FileChangeSummary {
  id: number;
  path: string;
  changeAction: ChangeAction;
  previousRevision: number | null;
  summary: string | null;
  skippedReason: string | null;
  agentSkips: AgentSkip[];
}

/**
 * Der Übersprung **eines** Agenten an einer Dateiänderung.
 *
 * Der Name steht mit dabei, weil eine Kennung allein keine Auskunft ist: Wer die
 * Datei ansieht, muss lesen können, *welcher* Agent sie nicht gesehen hat.
 */
export interface AgentSkip {
  agentId: number;
  agentName: string;
  reason: string;
}

/**
 * Der Nachbar einer Revision: Kennung und Nummer.
 *
 * Beides, weil beides gebraucht wird — die Kennung führt hin, die Nummer steht
 * am Schalter. „Weiter zu r4750" ist eine Auskunft, ein blanker Pfeil ist keine.
 */
export interface RevisionNeighbour {
  id: number;
  number: number;
}

/**
 * Eine Revision samt ihrer Dateiliste — eine Antwort, so wie die Ansicht sie
 * öffnet.
 *
 * `previous` und `next` sind die Nachbarn desselben Projekts, nach Nummer.
 * `null` heißt: In diese Richtung gibt es keine — und nicht, dass niemand
 * nachgesehen hätte.
 */
export interface RevisionDetail {
  revision: Revision;
  previous: RevisionNeighbour | null;
  next: RevisionNeighbour | null;
  fileChanges: FileChangeSummary[];
  /**
   * Der Commit-Kommentar in Abschnitten, mit den erkannten **Ticketverweisen**.
   * Aneinandergesetzt ergeben sie wieder genau `revision.message`.
   */
  messageParts: MessagePart[];
}

/**
 * Ein Abschnitt des Commit-Kommentars: gewöhnlicher Text, oder ein erkannter
 * **Ticketverweis** mit der Adresse, unter der der Vorgang steht.
 *
 * **Geschnitten hat der Server.** Woran ein Verweis erkannt wird, ist ein
 * regulärer Ausdruck aus der Konfiguration — ihn hier ein zweites Mal
 * auszulegen wären zwei Auslegungen derselben Einstellung. Die Oberfläche hat
 * hier nichts zu entscheiden: Sie zeichnet die Abschnitte der Reihe nach und
 * macht die mit Adresse anklickbar.
 *
 * `address` heißt **nicht**, dass es den Vorgang gibt — gefragt wird das
 * Ticketsystem dabei nichts.
 */
export interface MessagePart {
  text: string;
  address: string | null;
}

/**
 * Was eine Zeile innerhalb eines Hunks ist. Daran hängt, ob an ihr überhaupt ein
 * Befund liegen darf: Eine `removed`-Zeile gibt es in der begutachteten Revision
 * nicht mehr, und damit trägt sie keinen (ADR-0019).
 */
export type DiffLineKind = "context" | "added" | "removed";

/**
 * Eine Zeile des Diffs, mit ihrer Nummer auf der alten und der neuen Seite.
 *
 * Beide Nummern, weil ein Befund auf Datei @ Revision zeigt und nie auf eine
 * Position innerhalb des Diffs (ADR-0006).
 */
export interface DiffLine {
  kind: DiffLineKind;
  oldLine: number | null;
  newLine: number | null;
  text: string;
}

/** Ein Abschnitt des Diffs zwischen zwei `@@`-Köpfen. */
export interface DiffHunk {
  oldStart: number;
  oldCount: number;
  newStart: number;
  newCount: number;
  heading: string;
  lines: DiffLine[];
}

/**
 * Eine Dateiänderung mit allem, was die beiden Darstellungen brauchen: die
 * strukturierten Hunks und der vollständige Dateiinhalt. **Kein HTML** — die
 * Oberfläche rendert selbst, sonst hinge an keiner Zeile ein Anker (ADR-0008).
 *
 * `contentRevision` ist die Revision, in der der gelieferte Inhalt steht: die
 * begutachtete, und bei einer gelöschten Datei die Vorgängerrevision — in der
 * begutachteten gibt es sie nicht mehr.
 *
 * `sourceEncoding` nennt die Kodierung, aus der Diff und Inhalt beim Ablegen
 * nach UTF-8 gewandelt wurden (ADR-0023). `null` heißt: Sie kamen als UTF-8 —
 * nicht, dass niemand nachgesehen hätte.
 */
export interface FileChangeDetail extends FileChangeSummary {
  revisionId: number;
  projectId: number;
  revisionNumber: number;
  contentRevision: number;
  sourceEncoding: string | null;
  hunks: DiffHunk[];
  content: string | null;
}

/**
 * Die Befundart: wogegen ein Befund verstößt (Glossar, „Befundart").
 *
 * Die **Werte** bleiben deutsch, weil das Glossar sie festlegt und sie so in der
 * Datenbank stehen (ADR-0009). Eine geschlossene Menge, und geprüft wird sie im
 * Server — hier steht sie, damit der Dialog nichts anderes anbieten kann.
 */
export type FindingKind = "syntax" | "konvention" | "nicht-produktiv" | "struktur" | "migration";

/** Der Schweregrad in drei Stufen (Glossar, „Schweregrad"). */
export type Severity = "hinweis" | "mittel" | "schwer";

/**
 * Der **Lebenszyklus** eines Befundes: steht hier noch Arbeit (ADR-0017).
 *
 * Englisch wie jede geschlossene Menge, die das Glossar nicht selbst festlegt
 * (ADR-0009). Er allein entscheidet, ob eine Stelle im Vorrat steht — ob die
 * Maschine recht hatte, sagt die `Qualification` daneben und in einem eigenen
 * Feld.
 *
 * `fixed` und `dismissed` setzt ein Mensch: das eine sagt, dass die Stelle in
 * Ordnung gebracht wurde, das andere nur, dass hier nicht weitergearbeitet wird.
 * `superseded` setzt der Poller, sobald eine spätere Revision die verankerten
 * Zeilen ändert, und ist ausdrücklich keine Aussage.
 */
export type FindingStatus = "open" | "fixed" | "dismissed" | "superseded";

/**
 * Die **Qualifizierung** eines Befundes: hatte die Maschine recht (ADR-0017).
 *
 * Die zweite Achse neben dem Lebenszyklus, unabhängig von ihm; sie allein
 * füttert die Abweichungskennzahl. `null` steht daneben und ist kein Wert der
 * Menge: Ein Befund, über den noch niemand geurteilt hat, behauptet nichts —
 * weder für den Agenten noch gegen ihn.
 */
export type Qualification = "confirmed" | "rejected" | "acknowledged";

/**
 * Was der Filter des **Befundvorrats** auf dieser Achse annimmt: die drei
 * Qualifizierungen — und **unbewertet** (#185).
 *
 * `"unjudged"` ist kein vierter Wert der Qualifizierung und wird an keinem Befund
 * stehen: Es ist die **Abwesenheit** eines Urteils, gerechnet an der **Stelle**
 * und nicht am einzelnen Befund — dieselbe Regel wie `judgedSite()` in
 * `views/findings.ts`, verneint. Deshalb ein eigener Typ neben `Qualification`
 * und keine Erweiterung von ihr; wer eine Qualifizierung anzeigt, soll diesen
 * Wert gar nicht erst in der Hand haben.
 *
 * `null` heißt an dieser Stelle weiterhin „danach wird nicht gefiltert".
 */
export type QualificationFilter = Qualification | "unjudged";

/**
 * Ein Befund, wie die Befundleiste ihn bekommt: **der vollständige Anker** und
 * die Qualifizierung.
 *
 * Der Anker ist Projekt, Pfad, Revision und Zeilenbereich — nie eine Position
 * innerhalb eines Diffs (ADR-0006). Genau daran springt die Leiste zurück: Sie
 * sucht die Dateiänderung über den Pfad und die Seite über die Revision.
 */
export interface Finding {
  id: number;
  projectId: number;
  path: string;
  revisionNumber: number;
  startLine: number;
  endLine: number;
  kind: FindingKind;
  severity: Severity;
  body: string;
  /**
   * Die **Folge**: was daraus folgt, wenn der Befund so bleibt — was bricht,
   * wann, und für wen. `null` heißt „kein Wert abgelegt" — Altbestand aus der
   * Zeit vor dem Pflichtfeld und jeder Befund eines Kontrollmusters, dessen
   * Folge im festen Meldungstext des Musters steht und nicht hier. Die Ansicht
   * schreibt dort nichts Erfundenes hin.
   */
  consequence: string | null;
  /**
   * Der **Korrekturvorschlag**: wie die Stelle in Ordnung zu bringen wäre —
   * Prosa, ein bis zwei Sätze, kein Codevorschlag. `null` heißt „keiner
   * abgelegt" und ist ein gültiges Ergebnis: Ein Befund ohne benennbare Folge
   * ist keiner, ein Befund ohne benennbare Korrektur sehr wohl. Die Ansicht
   * erfindet dort nichts, und der Vorschlag sagt nicht, dass jemand die Änderung
   * abgenommen hat.
   */
  remedy: string | null;
  /**
   * Der **Titel**: die eine Zeile, die sagt, worum es geht. Leer heißt „aus
   * einem Lauf vor der Titelpflicht" — die Ansicht schreibt dort keinen
   * erfundenen hin.
   */
  title: string;
  /** Der **Lebenszyklus**: steht hier noch Arbeit. */
  status: FindingStatus;
  /**
   * Wann der Befund den Vorrat verlassen hat — bei „behoben" wie bei „verworfen"
   * und „überschrieben". `null` heißt: Er hat ihn nicht verlassen.
   */
  statusChangedAt: string | null;
  /**
   * Wer ihn auf „behoben" oder „verworfen" gesetzt hat. „Überschrieben" setzt
   * der Poller, und der ist keine Person. Daran entscheidet die Leiste, ob sie
   * die Rücknahme anbietet — zurücknehmen kann sie, wer sie gesetzt hat.
   */
  statusChangedBy: number | null;
  /**
   * Die **Qualifizierung**: hatte die Maschine recht. Sie steht **neben** dem
   * Lebenszyklus und nicht statt seiner — „bestätigt und offen" ist der
   * Normalfall und der bestbelegte Arbeitsauftrag im System. `null` heißt: Hier
   * hat noch niemand geurteilt.
   */
  qualification: Qualification | null;
  /** Wann qualifiziert wurde. `null` im Altbestand — erhoben wurde es nie. */
  qualifiedAt: string | null;
  /**
   * **Wer** qualifiziert hat, mit Namen. Das Urteil gilt für alle, und ein
   * globales Urteil, das niemand unterschreibt, kann niemand hinterfragen
   * (ADR-0022). Leer heißt: Es gibt keines — nicht „von unbekannt".
   */
  qualifiedBy: string;
  /**
   * Wann der Befund **entstanden** ist. Daran hängt der Zeitverlauf einer
   * Stelle: Was drei Agenten und die Kontrolle über dieselben Zeilen gesagt
   * haben, liest man in der Reihenfolge, in der es gesagt wurde.
   *
   * `null` heißt „nicht mitgeliefert" — die Gegenüberstellung vergleicht Anker
   * und Qualifizierung und braucht keinen Zeitpunkt.
   */
  createdAt: string | null;
}

/**
 * Woher ein Befund stammt (ADR-0010). Dieselbe geschlossene Menge, die der
 * Server führt; die deutschen Bezeichnungen stehen in der Oberfläche und nicht
 * im Wert (ADR-0009).
 */
export type FindingOrigin = "agent" | "pattern" | "person";

/**
 * Ein Befund, wie die Befundleiste ihn bekommt: der Befund und sein **Urheber**.
 *
 * `own` ist nicht aus der Herkunft abzulesen — ein Befund eines Kollegen trägt
 * dieselbe Herkunft `person` wie ein eigener —, und an dieser Unterscheidung
 * hängen Ändern und Löschen. Der Server zieht dieselbe Grenze an derselben
 * Tatsache.
 */
export interface RevisionFinding extends Finding {
  origin: FindingOrigin;
  /** Der Anzeigename der Person, der Name des Agenten, die Beschriftung des Musters. */
  author: string;
  own: boolean;
  /**
   * Die Versuche, diesen Befund zu **widerlegen** — leer, wenn es keinen gab.
   *
   * Eine Liste und kein einzelner Wert: Mehrere Widerlegungen desselben Befundes
   * stehen nebeneinander. Sie ändern am Befund nichts; wie laut ein widerlegter
   * Befund noch ist, entscheidet allein die Anzeige.
   */
  refutations: Refutation[];
}

/**
 * Was bei dem Versuch herauskam, einen Befund zu widerlegen.
 *
 * Der Auftrag lautet zu **widerlegen** und nicht zu bestätigen: Übereinstimmung
 * misst Übereinstimmung, nicht Richtigkeit. Deshalb ist `unclear` kein halbes
 * `upheld` — im Zweifel gilt der Befund als nicht gehalten.
 */
export type RefutationResult = "upheld" | "refuted" | "unclear";

export interface Refutation {
  result: RefutationResult;
  /** Der Name des widerlegenden Agenten — nie derselbe wie der des erhebenden. */
  author: string;
  reason: string;
  /** Wann der Versuch stattfand — er steht im Zeitverlauf der Stelle an seinem Platz. */
  createdAt: string | null;
}

/**
 * Woraus ein **Ausschnitt** geschnitten ist — die Angabe, an der die Ansicht den Satz
 * über dem Ausschnitt wählt.
 *
 * `content` ist der abgelegte Dateiinhalt im Stand des Ankers und der Regelfall;
 * `diff` bleibt für den Stand, der nicht abgelegt ist — dann fehlt, was zwischen
 * zwei Abschnitten liegt. `none` heißt: Zu diesen Zeilen liegt kein Text im
 * Bestand, und die Ansicht sagt das hin, statt eine leere Fläche zu zeigen.
 */
export type ExcerptSource = "content" | "diff" | "none";

/**
 * Der **Ausschnitt** zu einer Stelle: ihre Zeilen samt ein paar Zeilen Zusammenhang
 * (Glossar, „Ausschnitt").
 *
 * Er trägt den Anker, über den er zu seiner Stelle gehört, und **keine Kennung**:
 * Eine Stelle wird bei der Abfrage gerechnet und hat keine (ADR-0015). Verbunden
 * wird über Pfad, Revision und Zeilenbereich — dieselben vier Angaben, aus denen
 * die Ansicht ihre Stellen bildet.
 *
 * `side` ist die Seite, auf der die Zeilen der Stelle zählen; daran erkennt die
 * Ansicht, welche Zeilen des Ausschnitts die angekerten sind. `truncated` sagt, dass
 * der Bereich länger ist als der gezeigte Anfang.
 */
export interface SiteExcerpt {
  path: string;
  revisionNumber: number;
  startLine: number;
  endLine: number;
  side: "old" | "new";
  source: ExcerptSource;
  truncated: boolean;
  lines: DiffLine[];
}

/**
 * Die Befunde einer Revision, wie eine Person sie sieht.
 *
 * Seit ADR-0013 sind es **alle** — die der Agenten, der Kontrolle und der
 * Kollegen —, und zwar ohne Abgabe. `blind` sagt, ob für diese Person und diese
 * Revision der geschlossene Modus gilt; dann sind es wieder ausschließlich ihre
 * eigenen. Beides entscheidet die API und nicht die Oberfläche: Ein Modus, den
 * nur das Frontend einhält, ist keiner.
 *
 * `submitted` ist die Abgabe der angemeldeten Person: Danach steht der Bestand.
 *
 * `excerpts` sind die **Ausschnitte** zu den Stellen dieser Befunde — einer je Stelle
 * und nicht einer je Befund. Sie kommen in derselben Antwort, weil sie zu
 * derselben Frage gehören: Eine Stelle ohne ihren Code ist eine Adresse ohne das,
 * was dort steht.
 */
export interface RevisionFindings {
  findings: RevisionFinding[];
  excerpts: SiteExcerpt[];
  /**
   * Der **Vermerkstand** je Befund — nur zu den Befunden, an denen einer hängt.
   * Was fehlt, ist unvermerkt: `0` und `false` sind die Vorbelegung.
   */
  followUpMarks: FollowUpTally[];
  submitted: boolean;
  blind: boolean;
  /**
   * Kommt die Blindheit aus der **Blindabsicht** zu dieser Revision? Nur dann
   * trägt der Kopf des Befundprotokolls den Weg zurück — wer `blind_review`
   * trägt, arbeitet blind, ohne je einen Knopf gedrückt zu haben.
   */
  blindRequested: boolean;
}

/**
 * Der **Vermerkstand** eines Befundes: wie viele Menschen ihn nachgehalten haben
 * wollen und ob die angemeldete Person darunter ist (Glossar, „Nachhalten";
 * ADR-0012).
 *
 * Er steht **neben** den Befunden und nicht in ihnen, wie der Ausschnitt auch:
 * Ein Vermerk ist ein eigener Vorgang mit eigenem Urheber, eigenem Zeitpunkt und
 * eigener Begründung — und kein Feld des Befundes.
 *
 * `mine` sagt eine Tatsache und keine Erlaubnis: An diesem Befund steht ein
 * Vermerk dieser Person. Ob sie ihn zurücknehmen darf, prüft der Server noch
 * einmal, wenn der Knopf gedrückt wird.
 */
export interface FollowUpTally {
  findingId: number;
  total: number;
  mine: boolean;
}

/**
 * Ein **Nachhalte-Vermerk**, wie die Liste für `management` ihn zeigt.
 *
 * Der Befund steht vollständig darin, weil die Zeile ihn nennt: Ohne Ort,
 * Zeilenbereich und Titel wäre sie „jemand hat etwas nachgehalten" und sonst
 * nichts. `revisionId` ist das Sprungziel — `null` heißt, dass keine Revision
 * diese Zeilen zeigt, und dann führt von der Zeile kein Weg hinein.
 */
export interface FollowUpMark {
  id: number;
  /** Der Anzeigename der Person, die nachgehalten haben will. */
  person: string;
  /**
   * Die **Begründung** — der Ertrag gegenüber einer bloßen Zahl: Es liegt nicht
   * nur der Befund vor, sondern die Absicht des Menschen.
   */
  reason: string;
  createdAt: string;
  projectName: string;
  revisionId: number | null;
  finding: Finding;
}

/** Die Zahl der Vermerke in einem Kalendermonat — `YYYY-MM`. */
export interface FollowUpMonth {
  month: string;
  marks: number;
}

/**
 * Der Rückstand, mit dem v2 an Tag eins arbeitet — samt den Zahlen, an denen die
 * Bedingung hängt, ob v2 überhaupt anfängt (ADR-0012).
 *
 * Die Monate stehen vollständig da, auch die leeren: Gerade die Lücken sind hier
 * die Aussage.
 */
export interface FollowUpMarks {
  marks: FollowUpMark[];
  total: number;
  months: FollowUpMonth[];
}

/** Wonach die Liste der Vermerke sortiert wird. */
export type FollowUpMarkOrder = "created_at" | "person";

/**
 * Wer den Vermerk gesetzt hat: „ich" und sonst nichts.
 *
 * Eine fremde Person lässt sich nicht setzen — der Server nimmt sie aus der
 * Sitzung. „Die Vermerke von Müller" ist eine Personalfrage und keine
 * Arbeitsfrage (ADR-0028, „Was nicht entschieden wird").
 */
export type MarkAuthor = "me";

export interface FollowUpMarkQuery {
  sort: FollowUpMarkOrder;
  descending: boolean;
  /**
   * Der Filter **„von mir vermerkt"** — Urheberschaft: Ich habe diesen Vermerk
   * gesetzt. Nicht dasselbe wie „an meinem Code" im Befundvorrat, das die
   * Betroffenheitsrelation trägt (ADR-0028).
   */
  markedBy: MarkAuthor | null;
}

/**
 * Nachhalten und zurücknehmen, am einzelnen Befund.
 *
 * Beide Wege stehen unter dem **Befund** und nicht unter der Revision: Derselbe
 * Befund wird aus zwei Flächen heraus vermerkt — aus dem Befundverlauf und aus
 * der Gegenüberstellung.
 */
export interface FollowUpApi {
  addFollowUpMark(findingId: number, reason: string): Promise<void>;
  withdrawFollowUpMark(findingId: number): Promise<void>;
}

export interface FollowUpMarksApi {
  followUpMarks(query: FollowUpMarkQuery): Promise<FollowUpMarks>;
}

/**
 * Was das Anlegen schickt: die Markierung und die Qualifizierung.
 *
 * `fileChangeId` und `revisionNumber` statt eines fertigen Ankers — Projekt und
 * Pfad leitet der Server aus der Dateiänderung ab; sie sind nichts, was ein
 * Client frei setzen dürfte.
 */
export interface FindingDraft {
  fileChangeId: number;
  revisionNumber: number;
  startLine: number;
  endLine: number;
  kind: FindingKind;
  severity: Severity;
  body: string;
  title: string;
}

/**
 * Was sich an einem Befund ändern lässt: die Qualifizierung, nicht der Anker.
 * Ein Befund, der die Stelle wechselt, ist ein anderer Befund.
 */
export interface FindingQualification {
  kind: FindingKind;
  severity: Severity;
  body: string;
  title: string;
}

export interface FindingApi {
  findings(revisionId: number): Promise<RevisionFindings>;
  createFinding(revisionId: number, draft: FindingDraft): Promise<Finding>;
  updateFinding(
    revisionId: number,
    findingId: number,
    qualification: FindingQualification,
  ): Promise<Finding>;
  deleteFinding(revisionId: number, findingId: number): Promise<void>;
}

/**
 * Die Abgabe. Ihre Existenz ist die Grenze des geschlossenen Modus: Vor ihr sind
 * die eigenen Befunde änderbar und fremde unsichtbar, ab ihr steht der Bestand.
 */
export interface Submission {
  id: number;
  revisionId: number;
  submittedAt: string;
}

/**
 * Eine Zuordnung: ein menschlicher und ein maschineller Befund an derselben
 * Stelle — oder einer von beiden allein.
 *
 * **Beides ist zu unterscheiden**: Zwei Seiten heißt Übereinstimmung, eine leere
 * Seite heißt Abweichung. `kindMatches` und `severityMatches` gibt es nur, wenn
 * es zwei Seiten zu vergleichen gibt; sie gehen ausdrücklich **nicht** in den
 * Treffer ein — der hängt allein am Ort.
 */
export interface FindingMatch {
  id: number;
  humanFinding: Finding | null;
  machineFinding: Finding | null;
  kindMatches: boolean | null;
  severityMatches: boolean | null;
}

/** Die Gegenüberstellung eines Agenten: seine Zuordnungen zu dieser Revision. */
export interface ComparisonAgent {
  agentId: number;
  agentName: string;
  matches: FindingMatch[];
}

/**
 * Die dritte Spalte: was die **Kontrolle** zu dieser Revision beigetragen hat
 * (ADR-0010).
 *
 * Jede ihrer Zuordnungen trägt einen Kontrollbefund; `humanFinding` sagt, ob
 * auch ein Mensch dieselbe Stelle gesehen hat. **Übersehenes gibt es hier
 * nicht** — ein menschlicher Befund, den kein Muster deckt, ist keine Abweichung
 * der Kontrolle, sondern eine Stelle, über die sie nichts behauptet.
 *
 * `coverage` ist die Deckungszahl und `null`, wenn die Kontrolle nichts gefunden
 * hat: Eine Null hieße „nichts gedeckt" und wäre eine Aussage über einen
 * Vergleich, den es nie gab.
 */
export interface ControlComparison {
  matches: FindingMatch[];
  matched: number;
  unmatched: number;
  findings: number;
  coverage: number | null;
}

/**
 * Die Gegenüberstellung einer Revision.
 *
 * **Vor der Abgabe ist sie leer**, und das entscheidet der Server: Er liefert die
 * fremden Befunde gar nicht erst aus (ADR-0007). `submitted` ist deshalb keine
 * Anzeigelogik, sondern die Auskunft, warum nichts da ist.
 */
export interface Comparison {
  submitted: boolean;
  submittedAt: string | null;
  agents: ComparisonAgent[];
  control: ControlComparison;
  /**
   * Der **Vermerkstand** je Befund dieser Fläche. Sie zeigt die Agentenbefunde
   * nach der Abgabe, und auf ihnen wird ebenso nachgehalten (ADR-0012).
   */
  followUpMarks: FollowUpTally[];
}

/**
 * Die Abweichungskennzahl eines Agenten.
 *
 * **Sie ist gerechnet, nicht gespeichert** (ADR-0007). Alle Zählungen stehen
 * einzeln daneben, damit sich die Quote nachrechnen lässt — und damit eine
 * spätere, andere Formel die Reihe nicht entwertet.
 *
 * `deviation` ist `null`, wenn es keine einzige Zuordnung gibt: Eine Null hieße
 * „vollkommene Übereinstimmung" und wäre eine Aussage über einen Vergleich, den
 * es nie gab.
 */
export interface Deviation {
  agentId: number;
  agentName: string;
  matched: number;
  missed: number;
  unmatched: number;
  kindMatches: number;
  severityMatches: number;
  confirmed: number;
  rejected: number;
  bothEmpty: number;
  findings: number;
  deviation: number | null;
}

/**
 * Ein Fenster der **Abdeckung** (Glossar, „Abdeckung"; ADR-0021).
 *
 * Zwei Zahlen und keine Quote: `responsible` sind die Revisionen, zu denen ein
 * Agent in diesem Fenster beauftragt ist, `complete` davon die, bei denen jeder
 * seiner Aufträge erledigt ist. Der Anteil wird in der Anzeige gerechnet, damit er
 * sich nachrechnen lässt.
 *
 * `responsible: 0` heißt „hierüber gibt es nichts zu sagen" — dann steht ein
 * Gedankenstrich und nicht hundert Prozent.
 */
export interface CompletionTally {
  responsible: number;
  complete: number;
}

/**
 * Die vier rollenden Zeitfenster einer Zeile, gerechnet ab der Uhr des Servers
 * rückwärts (ADR-0021).
 *
 * Vier Fenster statt einer Zahl, weil eine einzige über den ganzen Bestand den
 * Stillstand verschweigt: Ein Agent, der seit gestern nichts mehr abarbeitet,
 * fällt in „Seit Beginn" um Bruchteile und in „24 Stunden" sofort auf.
 */
export interface CompletionWindows {
  last24Hours: CompletionTally;
  last7Days: CompletionTally;
  last30Days: CompletionTally;
  sinceStart: CompletionTally;
}

/** Die Abdeckung eines Agenten über den ganzen Bestand. */
export interface AgentCompletion extends CompletionWindows {
  agentId: number;
  agentName: string;
}

/**
 * Die **Abdeckung**: die Zeile „mindestens ein Agent" über die Revisionen und je
 * nicht stillgelegtem Agenten eine Zeile über den Betrieb.
 *
 * `anyAgent` steht als eigenes Feld und nicht als Agentenzeile ohne Kennung: Ein
 * magischer Wert müsste an jeder Stelle ausgenommen werden, an der über die
 * Agenten gerechnet wird.
 */
export interface Completion {
  anyAgent: CompletionWindows;
  agents: AgentCompletion[];
}

/**
 * Ein Modellaufruf, wie der Nachvollzug ihn zeigt: Anfrage, Rohantwort und das
 * **aufgelöste Basismodell** (ADR-0005).
 *
 * Das Token des Endpunkts steht nicht darin — es verlässt den Server nie.
 */
export interface ModelCall {
  id: number;
  agentId: number;
  agentName: string;
  endpointId: number;
  endpointName: string;
  presetIdentifier: string;
  resolvedBaseModel: string;
  request: string;
  rawResponse: string | null;
  durationMs: number | null;
  promptTokens: number | null;
  completionTokens: number | null;
  createdAt: string;
}

/** Der Zustand eines Prüfauftrags. */
export type ReviewJobState = "pending" | "claimed" | "done" | "failed";

/**
 * Die Art eines Prüfauftrags — und damit zugleich, worauf sein Bezug zeigt: die
 * Begutachtung auf eine Dateiänderung, die Synthese auf eine Revision, die
 * Kontrolle wieder auf eine Dateiänderung, die Widerlegung auf einen Befund.
 *
 * Die Werte sind englisch wie jeder andere Bezeichner (ADR-0009); ihre deutschen
 * Beschriftungen stehen an einer Stelle, in `views/reviewJobs.ts`.
 */
export type ReviewJobKind = "review" | "synthesis" | "control" | "refutation";

/**
 * Die Arten in der Reihenfolge, in der eine Fläche sie nebeneinanderstellt.
 *
 * Sie steht hier und nicht in der Ansicht, weil sie zur Form der Antwort gehört:
 * Jede Abbildung Art → Anzahl trägt genau diese vier Schlüssel, und geprüft wird
 * das an der Naht.
 */
export const REVIEW_JOB_KINDS: ReviewJobKind[] = [
  "review",
  "synthesis",
  "control",
  "refutation",
];

/**
 * Der Stand **einer** Warteschlange — des Paars aus Endpunkt und aufgelöstem
 * Basismodell (ADR-0003).
 *
 * Drei Zeilenformen kommen vor: beides gesetzt, das Basismodell offen (der Agent
 * hat noch nicht aufgelöst, ADR-0005) oder beides offen — dann ist es die
 * Kontrolle, und sie braucht keinen Slot (ADR-0010), weshalb `slots` dort `null`
 * ist und nicht Null.
 *
 * `pending` und `claimed` tragen alle vier Arten immer als Zahl. `parked` sagt,
 * wie viele der wartenden Aufträge einem stillgelegten Agenten gehören — sie
 * warten, gezogen werden sie nicht.
 */
export interface QueueLoad {
  endpointId: number | null;
  endpointName: string | null;
  baseModel: string | null;
  slots: number | null;
  pending: Record<ReviewJobKind, number>;
  claimed: Record<ReviewJobKind, number>;
  parked: number;
}

/**
 * Ein Prüfauftrag, der nicht weiterkommt: endgültig fehlgeschlagen oder geparkt,
 * weil sein Agent geparkt ist. `reason` trägt beides zusammengeführt.
 */
export interface StalledJob {
  id: number;
  kind: "review" | "synthesis";
  state: ReviewJobState;
  attempts: number;
  agentId: number;
  agentName: string;
  parked: boolean;
  reason: string | null;
  revisionNumber: number | null;
  path: string | null;
}

/**
 * Was ein **Sammelvorgang** ausgerichtet hat: eingereiht und liegengelassen (#94).
 *
 * Zwei Zahlen und nicht eine, weil die Aufträge geparkter Agenten liegen
 * bleiben — gezogen würden sie ohnehin nicht. Wer ihre Zahl nicht zu sehen
 * bekommt, hält die Liste danach für einen Fehler des Knopfes.
 */
export interface RequeueTally {
  requeued: number;
  skippedParked: number;
}

/**
 * Abgabe und Gegenüberstellung.
 *
 * Das Qualifizieren stand einmal hier — ein Weg je Befund, `PUT
 * /api/findings/{id}/status`, der allein die Qualifizierung setzte. Es gibt ihn
 * nicht mehr: Gesetzt wird über den **Handgriff an der Stelle**, der beide
 * Achsen zugleich setzt (`SiteVerdictApi`, ADR-0022). Zwei Schreibwege auf
 * dieselben zwei Felder wären genau der Zustand, den ADR-0022 beendet.
 */
export interface SubmissionApi {
  submit(revisionId: number): Promise<Submission>;
  comparison(revisionId: number): Promise<Comparison>;
}

/**
 * Was die **Gegenüberstellung** braucht: die Abgabe, den Nachvollzug für
 * Administratoren — und den Handgriff.
 *
 * Er steht hier, seit sie eine Fläche unter mehreren ist und keine Schranke
 * davor (ADR-0022): Bewertet wird auch hier an der Stelle, über denselben Weg
 * wie in der Detailansicht.
 */
export type ComparisonApi = SubmissionApi & ModelCallApi & SiteVerdictApi & FollowUpApi;

/**
 * Eine Datei mit Bestandsbefunden: der Anker ohne Zeilen, die Zahl der Befunde
 * und ihre Verteilung über die drei Schweregrade.
 *
 * Die Revision steht dabei, weil sie zur Datei gehört und nicht zur Ansicht: Ein
 * zweiter Bestandslauf löst seine Revision auf einen jüngeren Stand auf, und
 * dieselbe Datei trägt dann Befunde an zwei Ständen.
 */
export interface InventoryFile {
  path: string;
  revisionNumber: number;
  total: number;
  severities: Record<Severity, number>;
}

/**
 * Ein Kontrollmuster, wie der Filter es anbietet: die englische, stabile Kennung
 * und die deutsche Beschriftung daneben (ADR-0009).
 */
export interface ControlPatternOption {
  identifier: string;
  label: string;
}

/** Eine Seite der Dateiliste eines Bestandslaufs, samt der Muster für den Filter. */
export interface InventoryFiles {
  files: InventoryFile[];
  page: number;
  perPage: number;
  pages: number;
  total: number;
  patterns: ControlPatternOption[];
}

/** Eine Seite von Bestandsbefunden — mit `path` die einer einzelnen Datei. */
export interface InventoryFindings {
  findings: Finding[];
  page: number;
  perPage: number;
  pages: number;
  total: number;
}

/**
 * Die Frage an den Bestand. `null` heißt bei jedem Filter „danach wird nicht
 * gefiltert".
 */
export interface InventoryQuery {
  page: number;
  perPage: number;
  pattern: string | null;
  severity: Severity | null;
  /** Der **Lebenszyklus** — „was ist hier noch zu tun". */
  status: FindingStatus | null;
  /** Die **Qualifizierung** — „hatte die Maschine recht". Ein eigener Filter. */
  qualification: Qualification | null;
  path: string | null;
}

/**
 * Der Inhalt einer Datei im Stand einer Revision — der Zusammenhang, in dem ein
 * Bestandsbefund steht.
 *
 * Text und nie fertiges HTML (ADR-0008); die Zeilen zählt die Oberfläche, und
 * sie zählen in genau diesem Stand.
 */
export interface ProjectFile {
  path: string;
  revisionNumber: number;
  content: string;
}

/** Die Bestandsansicht: Projekte, Dateien, Befunde, Datei im Zusammenhang. */
export interface InventoryApi {
  monitoredProjects(): Promise<ProjectOption[]>;
  inventoryFiles(projectId: number, query: InventoryQuery): Promise<InventoryFiles>;
  inventoryFindings(projectId: number, query: InventoryQuery): Promise<InventoryFindings>;
  projectFile(projectId: number, path: string, revision: number): Promise<ProjectFile>;
}

/** Der Nachvollzug zu einem Befund. Erreichbar nur mit der Rolle `admin`. */
export interface ModelCallApi {
  modelCall(findingId: number): Promise<ModelCall>;
}

/** Die Abweichungskennzahl. Erreichbar mit `management` und `admin`. */
export interface DeviationApi {
  deviations(): Promise<Deviation[]>;
}

/**
 * Die Abdeckung. Sie steht an der Hauptansicht und nicht in der Verwaltung —
 * jeder Angemeldete bekommt sie.
 */
export interface CompletionApi {
  completion(): Promise<Completion>;
}

/**
 * Der **Weg zurück** aus dem geschlossenen Modus (v1.3-N-11).
 *
 * Er steht für sich, weil er an zwei Flächen gehört, der Weg hinein aber nur an
 * einer: Zurücknehmen kann man im Strom **und** im Kopf des Befundprotokolls —
 * dort merkt man, dass nichts zu sehen ist. Blind schalten kann man nur im Strom,
 * denn dort steht die Revision noch ungeöffnet.
 */
export interface BlindWithdrawalApi {
  /**
   * Die Blindabsicht zurücknehmen, solange nicht abgegeben ist.
   *
   * Der Server löscht sie und antwortet auch dann mit 200, wenn keine stand — die
   * Aussage danach ist dieselbe. Nach der Abgabe weist er mit 409 ab.
   */
  withdrawBlindReview(revisionId: number): Promise<void>;
}

export interface RevisionStreamApi extends CompletionApi, BlindWithdrawalApi {
  revisions(query: RevisionStreamQuery): Promise<RevisionStream>;
  /**
   * Diese Revision blind durchgehen (ADR-0013).
   *
   * Der Server weist mit 409 ab, wenn zuvor fremde Befunde ausgeliefert wurden;
   * der Strom fragt ohnehin im Takt nach und zeigt den Knopf danach ausgegraut.
   */
  requestBlindReview(revisionId: number): Promise<void>;
}

/**
 * Die Revisionsansicht. Der **Handgriff** gehört dazu und nicht zum Vorrat:
 * Bewertet wird, wo der Code steht (ADR-0022).
 */
export interface RevisionViewApi
  extends FindingApi,
    SubmissionApi,
    ModelCallApi,
    SiteVerdictApi,
    FollowUpApi,
    BlindWithdrawalApi,
    // Aus einem Befund wird hier ein Kontrollmuster (ADR-0033, ADR-0012): Der
    // Knopf steht am Befund, wo ein Mensch gerade über ihn geurteilt hat.
    PatternFromFindingApi,
    DeliveryApi {
  revision(id: number): Promise<RevisionDetail>;
  fileChange(id: number): Promise<FileChangeDetail>;
  /**
   * Die **Übergabe** zu einer ganzen Revision: derselbe kopierfertige Text wie
   * zu einer Stelle, nur eine Ebene höher (Glossar, „Übergabe").
   *
   * Zusammengestellt wird er im Server — nur dort liegen die Befundtexte, die
   * abgelegten Diff-Ausschnitte und die Reihenfolge, in der die Stellen stehen.
   */
  revisionHandoff(revisionId: number): Promise<string>;
  /**
   * Der **Befundvorrat** — für den einen Knopf, der aus dieser Revision heraus in
   * die nächste mit einer unbewerteten Stelle an eigenem Code führt (#185).
   *
   * Hier steht die eine Methode und nicht `FindingSitesApi` als Ganzes: Dessen
   * zweite Hälfte ist die **Übergabe zu einer Stelle**, und die gibt es aus dieser
   * Ansicht nicht — sie hat ihre eigene, zur ganzen Revision.
   */
  findingSites(query: SiteQuery): Promise<FindingSites>;
}

export interface RevisionApi extends RevisionStreamApi, RevisionViewApi {}

/**
 * Ein Endpunkt, so wie der Browser ihn sieht: **ohne Token**.
 *
 * Das Token wird geschrieben und nie zurückgelesen — es gibt hier kein Feld
 * dafür, auch kein verkürztes, damit die Oberfläche gar nicht erst eines
 * anzeigen kann.
 */
export interface Endpoint {
  id: number;
  name: string;
  address: string;
  maxParallelCalls: number;
}

/** Was der Dialog über einen Endpunkt schickt. `token: null` lässt das hinterlegte stehen. */
export interface EndpointDraft {
  name: string;
  address: string;
  token: string | null;
  maxParallelCalls: number;
}

/**
 * Ein Preset des Endpunkts, mit der Kennung seines Basismodells.
 *
 * Nur Kennungen, keine Anzeigenamen: Die API verlangt die ID
 * (`api-devtool-qwen36-27b`, nicht `Api-Devtool-Qwen36-27b`).
 */
export interface Preset {
  identifier: string;
  baseModel: string;
}

/** Ein Basismodell mit gemessener Slotzahl und bekanntem Kontextfenster. */
export interface BaseModel {
  id: number;
  endpointId: number;
  identifier: string;
  slots: number;
  contextWindow: number | null;
}

/** Der Zustand eines Agenten. `parked` vergibt der Arbeiter, nicht der Dialog. */
export type AgentState = "active" | "retired" | "parked";

/** Die Parameter eines Agenten — Temperatur, Token-Grenze, was der Endpunkt sonst versteht. */
export type AgentParameters = Record<string, string | number | boolean>;

export interface Agent {
  id: number;
  name: string;
  endpointId: number;
  presetIdentifier: string;
  parameters: AgentParameters;
  contextLimit: number;
  reviewPromptTemplate: string;
  synthesisPromptTemplate: string;
  lastSeenBaseModel: string | null;
  state: AgentState;
  parkedReason: string | null;
  /**
   * Der Stichtag der Abdeckung: Revisionen davor sind für diesen Agenten nicht
   * zuständig (Glossar, „Abdeckung"). `null` heißt „keine Einschränkung".
   */
  activatedAt: string | null;
  /**
   * Der Schalter der Widerlegung: ob die Befunde **dieses** Agenten von einem
   * anderen gegengeprüft werden — die erhebende Seite, nicht die widerlegende.
   * Vorbelegung aus (#78).
   */
  refutationEnabled: boolean;
  /**
   * Ob **dieser** Agent aus den Befunden aller Agenten die eine
   * Review-Empfehlung einer Revision zieht (#79). Höchstens einer trägt ihn —
   * der Server erzwingt das —, und niemand trägt ihn vorbelegt.
   */
  synthesisAgent: boolean;
  /**
   * Der Schalter des Denkmodus (#119): Er hängt jeder Anfrage dieses Agenten
   * `chat_template_kwargs: {"enable_thinking": …}` an. Vorbelegung aus, auch
   * für den Altbestand — mit ihm steigt die Laufzeit je Aufruf gemessen von
   * 28-29 s auf 136-139 s.
   */
  reasoning: boolean;
}

/** Was der Dialog über einen Agenten schickt. `parked` steht nicht zur Wahl. */
export interface AgentDraft {
  name: string;
  endpointId: number;
  presetIdentifier: string;
  parameters: AgentParameters;
  contextLimit: number;
  reviewPromptTemplate: string;
  synthesisPromptTemplate: string;
  state?: "active" | "retired";
  refutationEnabled: boolean;
  synthesisAgent: boolean;
  reasoning: boolean;
}

/** Die Modellliste eines Endpunkts, gelesen für den Agenten-Dialog. */
export interface EndpointModels {
  presets: Preset[];
  baseModels: BaseModel[];
}

/**
 * Ein Projekt: ein überwachter Unterbaum des SVN-Repositories, kein eigenes
 * Repository.
 *
 * `lastProcessedRevision` ist der gesamte Fortschritt (ADR-0002).
 * `lastPollAt` ist ISO-8601 mit Zeitzone, `null` heißt: Der Poller lief für
 * dieses Projekt noch nie.
 *
 * `currentPhpVersion` ist die laufende Version, `plannedPhpVersions` sind die
 * geplanten Zielversionen — zwei Felder und nicht eines, weil die Kontrolle
 * gegen die laufende arbeitet und eine Liste ohne Auszeichnung, welche das ist,
 * ihr nichts nützt. Die Liste darf leer sein.
 */
export interface Project {
  id: number;
  name: string;
  svnPath: string;
  currentPhpVersion: string;
  plannedPhpVersions: string[];
  startRevision: number;
  lastProcessedRevision: number;
  active: boolean;
  lastPollAt: string | null;
  lastPollResult: string | null;
  /**
   * Die **Anzeigeschwelle**: der Schweregrad, unterhalb dessen Befunde in der
   * Vorbelegung des Befundvorrats nicht erscheinen. Erhoben und gespeichert wird
   * weiter alles; `hinweis` unterdrückt nichts.
   */
  displayThreshold: Severity;
}

/**
 * Die Projekte der Verwaltung.
 *
 * Das Ticketsystem steht hier **nicht** mehr (#112): Wohin ein Ticketverweis
 * zeigt, sagt seit der Korrektur an #101 die **Zuordnungsregel** und nicht das
 * Projekt. Gepflegt wird sie im eigenen Abschnitt „Ticketsystem".
 */
export interface ProjectAdministration {
  projects: Project[];
}

/**
 * Was der Dialog über ein Projekt schickt.
 *
 * `resetProgress` ist die ausdrückliche Bestätigung, dass eine versetzte
 * Startrevision den Fortschritt zurücksetzt. Sie fehlt, solange nichts zu
 * bestätigen ist — und der Server nimmt die Änderung ohne sie nicht an.
 */
export interface ProjectDraft {
  name: string;
  svnPath: string;
  currentPhpVersion: string;
  plannedPhpVersions: string[];
  startRevision: number;
  displayThreshold: Severity;
  resetProgress?: boolean;
}

/**
 * Eine **Zuordnungsregel** (#112): Sie bindet einen regulären Ausdruck an eine
 * **Ticketliste** im Ticketsystem.
 *
 * Die Vorsilbe wählt die Liste, die Zahl den Vorgang: `Dev435` findet die Regel
 * `(?i)\bdev(\d+)\b` → `yQos/dev-liste`. Genau eine Fanggruppe, denn sie
 * liefert die Nummer.
 *
 * `position` ist bedeutungstragend: Sie entscheidet, welche von zwei Regeln
 * gewinnt, die an derselben Stelle desselben Textes gleich lang treffen.
 *
 * `complaint` ist gesetzt, wenn diese Regel nichts erkennt — eine Zeile, die von
 * Hand in die Datenbank kam, denn die Maske weist so etwas ab. Sie steht dann in
 * der Liste mit ihrem Grund und nicht wie jede andere: Sonst ist „warum ist
 * Dev435 kein Link?" nur im Log zu beantworten.
 *
 * `prefix` gehört der **Ticketliste** und nicht dieser Regel (#131): Zwei Regeln,
 * die auf dieselbe Liste zeigen, nennen dasselbe Kürzel, und `null` heißt „keines
 * eingetragen" — dann steht in der Spalte „Ticket" `owner/repo#nummer`.
 */
export interface IssueReferenceRule {
  id: number;
  expression: string;
  /** Die Ticketliste als `owner/repo`. */
  target: string;
  position: number;
  prefix: string | null;
  complaint: string | null;
}

/** Was der Dialog über eine Zuordnungsregel schickt. */
export interface IssueReferenceRuleDraft {
  expression: string;
  target: string;
  /** Fehlt sie beim Anlegen, steht die Regel hinten und überholt keine. */
  position?: number;
  /**
   * Das Kürzel der **Ticketliste**. Leer nimmt es ihr — und damit beiden Regeln,
   * die auf sie zeigen. Fehlt das Feld ganz, bleibt es, wie es war.
   */
  prefix?: string;
}

/**
 * Das Ticketsystem, wie die Verwaltung es sieht: die **Instanz** und die
 * **Zuordnungsregeln**.
 *
 * Beides in einer Antwort, weil der Abschnitt beides braucht, um sagen zu
 * können, was fehlt: Ohne Instanz nützt die schönste Regel nichts, und ohne
 * Regel die schönste Instanz nicht. `null` heißt: keine eingestellt.
 */
/**
 * Die Personen, wie die Verwaltung sie sieht — und ob ein Zugang zum Chat
 * hinterlegt ist (#104).
 *
 * Beides in einer Antwort, weil der Abschnitt beides braucht, um sagen zu können,
 * was fehlt: Die Mattermost-Kennung lässt sich hier pflegen, aber ohne Zugang geht
 * keine Nachricht hinaus. Eine Maske, die gepflegt aussieht und trotzdem nichts
 * bewirkt, ist die unangenehmste Art, eine fehlende Einstellung zu erfahren.
 */
export interface PeopleAdministration {
  people: Person[];
  mattermostConfigured: boolean;
}

/**
 * Ein **Kontrollmuster**, wie die Musterverwaltung es sieht (ADR-0033).
 *
 * Seine **Aussage** lebt seit der ersten Etappe in den Daten und lässt sich hier
 * ändern: Name, Meldungstext, Befundart, Schweregrad. Die **Erkennung** steht
 * noch im Code — `sniffCode` nennt den Sniff und ist deshalb Auskunft und kein
 * Feld.
 *
 * `identifier` ist die stabile Kennung: Sie steht an jedem Befund, den dieses
 * Muster erzeugt hat, und wird nicht umbenannt.
 *
 * **Stillgelegt ist, was einen Zeitpunkt hat.** Kein zweites Kennzeichen daneben
 * — das wäre eine zweite Wahrheit über denselben Sachverhalt. `retiredByName`
 * darf fehlen: Die Saat der Migration hat für niemanden gehandelt.
 */
export interface ControlPattern {
  id: number;
  identifier: string;
  /**
   * **Woher die Erkennung kommt** — `sniff` aus dem Motor, `fragment` aus einem
   * Codefragment mit Platzhaltern (ADR-0033, zweite Etappe).
   *
   * Eine Eigenschaft des Musters und keine des Befundes: Die Befunde beider
   * Arten sind ununterscheidbar.
   */
  detection: string;
  /** Der Meldungscode des Motors — nur bei `sniff`. */
  sniffCode: string | null;
  /** Das Codefragment mit Platzhaltern — nur bei `fragment`. */
  fragment: string | null;
  /** Der Schnipsel, den das Muster treffen **muss**. */
  positiveExample: string | null;
  /** Der Schnipsel, den es **nicht** treffen darf. */
  negativeExample: string | null;
  label: string;
  message: string;
  kind: string;
  severity: string;
  active: boolean;
  retiredAt: string | null;
  retiredByName: string | null;
  retiredReason: string | null;
  /**
   * Der **Befund, aus dem dieses Muster entstanden ist** — `null` heißt „von
   * Hand angelegt" (ADR-0033, ADR-0012).
   *
   * Er ist die Auskunft, die in sechs Monaten die Frage „warum gibt es dieses
   * Muster" beantwortet, und er steht mit seinem Anker da: „aus Befund 412"
   * nennt keinen Ort.
   */
  originFinding: FindingAnchor | null;
  /** Wie viele Befunde dieses Muster erhoben hat. */
  findingCount: number;
  /**
   * Ob dieses Muster je über einen **Bestandslauf** gegangen ist.
   *
   * Ohne diese Angabe läse sich „keine Befunde" bei einem frisch angelegten
   * Muster als Entwarnung, die niemand gegeben hat: Es findet ab seiner
   * Entstehung und hat den Altbestand nie gesehen.
   */
  inventoried: boolean;
}

/**
 * Der **Anker** eines Befundes: Projekt, Pfad, Revision, Zeilenbereich
 * (ADR-0006) — nie eine Position innerhalb eines Diffs.
 */
export interface FindingAnchor {
  id: number;
  projectId: number;
  path: string;
  revisionNumber: number;
  startLine: number;
  endLine: number;
}

/**
 * Der **Entwurf eines Kontrollmusters aus einem Befund** (ADR-0033, ADR-0012).
 *
 * Er belegt **Fragment und Positivbeispiel** mit dem Codeausschnitt der Stelle
 * vor, auf die der Befund zeigt — und sonst nichts. Die Platzhalter setzt ein
 * Mensch, das Negativbeispiel kann aus dem Befund nicht kommen, und der Text des
 * Befundes ist die Aussage über *diese* Stelle, wo ein Muster über alle spricht.
 *
 * Die geschlossenen Mengen kommen mit, damit die Maske Art und Schwere daraus
 * wählen kann; vorbelegt ist keine von beiden.
 */
export interface PatternDraft {
  finding: FindingAnchor;
  fragment: string;
  positiveExample: string;
  kinds: string[];
  severities: string[];
}

/**
 * Was der Dialog über ein Kontrollmuster schickt.
 *
 * `fragment` steht nur bei einem Fragmentmuster darin — ein Sniffmuster nimmt
 * keines an. Das Beispielpaar darf bei einem Sniffmuster leer bleiben: Dort ist
 * der Sniff samt seinem Test im Repository der Test.
 */
export interface ControlPatternDraft {
  label: string;
  message: string;
  kind: string;
  severity: string;
  fragment?: string;
  positiveExample?: string;
  negativeExample?: string;
}

/**
 * Was der Dialog über ein **neues** Muster schickt.
 *
 * Angelegt wird ein Fragmentmuster und sonst keines: Ein Sniffmuster braucht
 * eine Klasse im Motor und kommt mit einem Release. Kennung, Fragment und
 * Beispielpaar sind deshalb Pflicht.
 */
export interface NewControlPattern {
  identifier: string;
  fragment: string;
  positiveExample: string;
  negativeExample: string;
  label: string;
  message: string;
  kind: string;
  severity: string;
  /**
   * Der Befund, aus dem dieses Muster entsteht — nur auf dem Weg vom Befund
   * hierher gesetzt. Ein von Hand angelegtes Muster nennt keinen.
   */
  originFindingId?: number;
}

/**
 * Die Kontrollmuster samt der geschlossenen Mengen, aus denen Art und Schwere
 * gewählt werden.
 *
 * Beides in einer Antwort, weil die Maske beides braucht: Eine Auswahlliste, die
 * die Oberfläche selbst führt, wäre eine zweite Wahrheit über eine Menge, die im
 * Server steht — und ihre Werte sind deutsch, weil das Glossar sie festlegt.
 */
export interface ControlPatternAdministration {
  patterns: ControlPattern[];
  kinds: string[];
  severities: string[];
}

export interface IssueTrackerAdministration {
  rules: IssueReferenceRule[];
  forgejoBaseAddress: string | null;
}

/**
 * Was ein angestoßener Bestandslauf zurückmeldet.
 *
 * `revisionNumber` ist die **einmal aufgelöste** Revision: Alle Befunde eines
 * Laufs hängen an ihr, und sie ist nie `HEAD`.
 *
 * `patterns` sind die Kontrollmuster, die dieser Lauf verarbeitet — beim ersten
 * Lauf alle, danach nur die seither hinzugekommenen. Eine leere Liste ist
 * deshalb kein Fehler, sondern die Auskunft, dass es nichts zu tun gab; die
 * Maske sagt das und zeigt keine Störung.
 */
export interface InventoryRun {
  id: number;
  projectId: number;
  revisionNumber: number;
  patterns: string[];
  files: number;
}

/**
 * Eine Person, wie der Verwaltungsdialog sie sieht: mit der ERP-Identität, an
 * die ihre lokal geführte Rolle gebunden ist.
 */
export interface Person {
  id: number;
  displayName: string;
  erpIdentity: string;
  role: Role;
  active: boolean;
  /**
   * **Blindes Begutachten**: Diese Person sieht bei ihrer Bearbeitung keine
   * fremden Befunde, bis sie abgegeben hat (ADR-0013).
   *
   * Eine eigene Achse neben der Rolle und kein vierter Rollenwert: Die Rollen
   * beschreiben Berechtigungsstufen, das Blindarbeiten ist eine Betriebsart.
   */
  blindReview: boolean;
  /**
   * Die **Chat-Kennung**: der Benutzername dieser Person im Chatsystem
   * (ADR-0027). `null` heißt „keine" — dann ist sie nicht per Direktnachricht
   * erreichbar, und geraten wird keine Adresse.
   *
   * Sie hängt an der Person und nicht an ihrer SVN-Kennung: Ein Mensch hat über
   * die Zeit mehrere SVN-Kennungen, an denen die Kennung mehrfach stünde und
   * auseinanderdriftete. Der Name trägt den Hersteller nicht — der steht in der
   * Oberfläche.
   */
  chatHandle: string | null;
  /**
   * Die **SVN-Kennungen**, die dieser Person zugeordnet sind, alphabetisch
   * (ADR-0028). Ein Mensch kann über die Zeit mehrere haben — deshalb eine
   * Liste und kein einzelner Wert.
   */
  svnIdentities: string[];
}

/**
 * Eine SVN-Kennung. `personId: null` heißt: Sie tauchte in einer Revision auf,
 * ohne jemandem zu gehören — genau die, die ein Administrator nachziehen soll.
 */
export interface SvnIdentity {
  id: number;
  identity: string;
  personId: number | null;
}

/**
 * Die Verwaltung. Erreichbar ist sie nur mit der Rolle `admin` — geprüft wird das
 * im Server bei jeder Anfrage und nicht hier.
 */
/**
 * **Aus einem Befund ein Kontrollmuster machen** (ADR-0033, ADR-0012) — der
 * Entwurf und das Anlegen.
 *
 * Ein eigener Vertrag, weil ihn zwei Flächen brauchen: die Musterverwaltung, die
 * von Hand anlegt, und die Befundansicht, in der der Knopf am Befund steht.
 * Erlaubt ist beides derselben Rolle — `admin`, wie jede Schreiboperation an der
 * Musterverwaltung.
 */
export interface PatternFromFindingApi {
  /**
   * Die **Vorbelegung** der Musteranlage aus einem Befund: Fragment und
   * Positivbeispiel aus der Stelle, auf die er zeigt. Alles andere kommt leer.
   */
  controlPatternDraft(findingId: number): Promise<PatternDraft>;
  /**
   * Ein **Fragmentmuster** anlegen — mit Fragment und Beispielpaar.
   *
   * Der Server fährt das Paar, bevor er speichert: Hält es nicht, kommt die
   * Meldung zurück, die nennt, welcher der beiden Schnipsel es war.
   */
  createControlPattern(draft: NewControlPattern): Promise<ControlPattern>;
}

export interface AdminApi extends ModelCallApi, PatternFromFindingApi {
  /** Was nicht weiterkommt: fehlgeschlagene und geparkte Aufträge samt Grund. */
  stalledJobs(): Promise<StalledJob[]>;
  /**
   * Erneut einreihen: **der einzelne** fehlgeschlagene Auftrag (#94).
   *
   * Er geht zurück auf wartend, sein Versuchszähler auf Null — der Knopfdruck eines
   * Menschen ist das Urteil, dass ein neuer Anlauf gerechtfertigt ist. Der
   * Fehlertext bleibt stehen; er sagt bis zum nächsten Abschluss weiterhin wahr,
   * woran es zuletzt scheiterte.
   */
  requeueJob(id: number): Promise<void>;
  /**
   * Erneut einreihen: **alle** fehlgeschlagenen Aufträge mit genau diesem
   * Fehlertext (#94).
   *
   * Der Text ordnet nur die Anzeige und ist keine Fehlerklasse — er sortiert, was
   * ein Mensch auswählt. Die Antwort nennt, wie viele eingereiht wurden und wie
   * viele als Aufträge geparkter Agenten liegen blieben.
   */
  requeueJobsWithError(reason: string): Promise<RequeueTally>;
  /**
   * Welche Art wartet wo: eine Zeile je Warteschlange, also je Paar aus Endpunkt
   * und aufgelöstem Basismodell.
   */
  queueLoad(): Promise<QueueLoad[]>;
  endpoints(): Promise<Endpoint[]>;
  createEndpoint(draft: EndpointDraft): Promise<Endpoint>;
  updateEndpoint(id: number, draft: EndpointDraft): Promise<Endpoint>;
  endpointModels(id: number): Promise<EndpointModels>;
  baseModels(): Promise<BaseModel[]>;
  sharpenBaseModel(id: number, slots: number, contextWindow: number | null): Promise<BaseModel>;
  agents(): Promise<Agent[]>;
  createAgent(draft: AgentDraft): Promise<Agent>;
  updateAgent(id: number, draft: AgentDraft): Promise<Agent>;
  releaseAgent(id: number): Promise<Agent>;
  /**
   * Das **rückwirkende Nachholen**: Prüfaufträge ab einer Startrevision, für
   * einen Agenten. Ein bewusster Anstoß — er kostet je Dateiänderung einen
   * Modellaufruf. Die Antwort sagt, wie viele Aufträge entstanden sind.
   */
  backfillAgent(id: number, fromRevision: number): Promise<number>;
  deleteAgent(id: number): Promise<void>;
  projects(): Promise<ProjectAdministration>;
  createProject(draft: ProjectDraft): Promise<Project>;
  updateProject(id: number, draft: ProjectDraft): Promise<Project>;
  /** Anhalten und Wiederaufnehmen: ein Knopfdruck, ohne die Stammdaten mitzuschicken. */
  pauseProject(id: number): Promise<Project>;
  resumeProject(id: number): Promise<Project>;
  /**
   * Den Bestandslauf anstoßen — ein bewusster Anstoß und kein Cron-Eintrag.
   * Gearbeitet wird danach vom Arbeiter, nachrangig zur laufenden Begutachtung.
   */
  startInventoryRun(id: number): Promise<InventoryRun>;
  /** Die Instanz des Ticketsystems und die Zuordnungsregeln der Ticketverweise. */
  issueReferenceRules(): Promise<IssueTrackerAdministration>;
  createIssueReferenceRule(draft: IssueReferenceRuleDraft): Promise<IssueReferenceRule>;
  updateIssueReferenceRule(
    id: number,
    draft: IssueReferenceRuleDraft,
  ): Promise<IssueReferenceRule>;
  deleteIssueReferenceRule(id: number): Promise<void>;
  /** Die Kontrollmuster samt der Mengen für Befundart und Schweregrad. */
  controlPatterns(): Promise<ControlPatternAdministration>;
  /** Ein Muster ändern — Kennung, Erkennungsart und Sniff bleiben, wie sie sind. */
  updateControlPattern(id: number, draft: ControlPatternDraft): Promise<ControlPattern>;
  /**
   * Stilllegen, mit **Pflichtbegründung**: Ein Muster, das ohne festgehaltenen
   * Grund schweigt, ist in sechs Monaten nicht mehr erklärbar. Die erhobenen
   * Befunde bleiben stehen, und kein Bestandslauf entsteht daraus.
   */
  retireControlPattern(id: number, reason: string): Promise<ControlPattern>;
  /** Wieder einschalten. Auch das stößt keinen Bestandslauf an. */
  reinstateControlPattern(id: number): Promise<ControlPattern>;
  /** Die Personen und die Auskunft, ob eine Direktnachricht überhaupt hinausgeht. */
  people(): Promise<PeopleAdministration>;
  /**
   * Die Rolle vergeben. Die Antwort ist eine Bestätigung und keine Person —
   * gelesen wird der neue Stand danach ohnehin wieder aus der Liste.
   */
  setRole(id: number, role: Role): Promise<void>;
  /** Blindes Begutachten schalten — senkrecht zur Rolle und deshalb ein eigener Weg. */
  setBlindReview(id: number, blindReview: boolean): Promise<void>;
  /**
   * Die Chat-Kennung setzen oder mit `null` leeren. Getrimmt und um ein
   * führendes `@` gekürzt wird im Server: Die Regel steht an einer Stelle, sonst
   * driften zwei auseinander.
   */
  setChatHandle(id: number, chatHandle: string | null): Promise<void>;
  svnIdentities(): Promise<SvnIdentity[]>;
  /** `null` löst die Zuordnung wieder, ohne die Kennung zu verlieren. */
  assignSvnIdentity(id: number, personId: number | null): Promise<void>;
}

export interface ApiClient
  extends StatusApi,
    VersionsApi,
    SessionApi,
    PasskeyApi,
    RevisionApi,
    FindingSitesApi,
    InventoryApi,
    AdminApi,
    DeviationApi,
    FollowUpMarksApi {}

export const httpClient: ApiClient = {
  async status(): Promise<ScaffoldStatus> {
    return toScaffoldStatus(await request("GET", "/api/status"));
  },

  async versions(): Promise<VersionHistory> {
    return toVersionHistory(await request("GET", "/api/versions"));
  },

  async signIn(credentials: Credentials): Promise<SessionState> {
    return toSessionState(
      await request("POST", "/api/sign-in", {
        username: credentials.username,
        password: credentials.password,
        device: device(),
      }),
    );
  },

  async passkeyChallenge(device: string): Promise<unknown> {
    // Unverändert weiter an den Gerätedialog: Wer die Probe dekodiert und neu
    // kodiert, bricht die Unterschrift.
    return request("POST", "/api/sign-in/challenge", { device });
  },

  async signInWithPasskey(assertion: PasskeyAssertion): Promise<SessionState> {
    return toSessionState(await request("POST", "/api/sign-in/passkey", { ...assertion }));
  },

  async passkeySetupChallenge(device: string): Promise<unknown> {
    return request("POST", "/api/passkey/challenge", { device });
  },

  async setUpPasskey(registration: PasskeyRegistration): Promise<void> {
    await request("POST", "/api/passkey", { ...registration });
  },

  async session(): Promise<SessionState> {
    return toSessionState(await request("GET", "/api/session"));
  },

  async signOut(): Promise<void> {
    await request("DELETE", "/api/session");
  },

  async currentPerson(): Promise<CurrentPerson> {
    return toCurrentPerson(await request("GET", "/api/people/me"));
  },

  async revisions(query: RevisionStreamQuery): Promise<RevisionStream> {
    return toRevisionStream(await request("GET", `/api/revisions?${asQuery(query)}`));
  },

  async findingSites(query: SiteQuery): Promise<FindingSites> {
    const body = await request("GET", `/api/finding-sites?${asSiteQuery(query)}`);
    const incomplete = fieldsOf(body)?.["incomplete_revisions"];
    const below = fieldsOf(body)?.["below_threshold"];

    if (typeof incomplete !== "number") {
      throw new ApiError("Die Antwort zu den Befunden nennt die fehlende Begutachtung nicht.");
    }

    // Ohne diese Zahl wüsste die Ansicht nicht, ob sie gerade etwas verschweigt
    // — und sie behauptete, vollständig zu sein.
    if (typeof below !== "number") {
      throw new ApiError("Die Antwort zu den Befunden nennt die Anzeigeschwelle nicht.");
    }

    return {
      sites: listOf(body, "sites").map(toSite),
      ...pageOf(body, "den Befunden"),
      projects: listOf(body, "projects").map(toProjectOption),
      incompleteRevisions: incomplete,
      belowThreshold: below,
    };
  },

  async handoff(site: Site): Promise<string> {
    return handoffTextOf(await request("GET", `/api/finding-sites/handoff?${asAnchor(site)}`));
  },

  async revisionHandoff(revisionId: number): Promise<string> {
    // Die Kennung steht in der Abfragezeichenkette und nicht im Pfad: Neben
    // dieser Route liegt `/api/revisions/{id}`, und ein `handoff` als Segment
    // wäre dort eine Kennung.
    return handoffTextOf(await request("GET", `/api/revisions/handoff?revision_id=${revisionId}`));
  },

  async deliveryPreparation(cut: DeliveryCut): Promise<DeliveryPreparation> {
    return toDeliveryPreparation(
      await request("GET", `/api/deliveries/preparation?${asCut(cut)}`),
    );
  },

  async deliver(cut: DeliveryCut, target: DeliveryTarget, preface: string): Promise<Delivered> {
    // Der Rumpf geht hier ausdrücklich **nicht** mit: Er entsteht im Server aus
    // dem Befundstand, und nur das Vorwort gehört dem Menschen.
    return toDelivered(
      await request("POST", "/api/deliveries", {
        // Der Weg steht ausdrücklich im Rumpf, so wie er im Vermerk steht (#104):
        // Eine Vorbelegung hieße, dass ein Aufruf ohne Weg irgendwohin geht.
        channel: "ticket",
        revision_id: cut.revisionId,
        ...(cut.anchor === null ? {} : anchorOf(cut.anchor)),
        owner: target.owner,
        repo: target.repo,
        number: target.number,
        preface,
      }),
    );
  },

  async deliverToChat(
    cut: DeliveryCut,
    recipient: ChatRecipient,
    preface: string,
  ): Promise<Delivered> {
    return toDelivered(
      await request("POST", "/api/deliveries", {
        channel: "chat",
        revision_id: cut.revisionId,
        ...(cut.anchor === null ? {} : anchorOf(cut.anchor)),
        person_id: recipient.personId,
        preface,
      }),
    );
  },

  async setSiteVerdict(anchor: SiteAnchor, verdict: Verdict, force: boolean): Promise<number> {
    return changedCountOf(
      await request("PUT", "/api/finding-sites/verdict", {
        ...anchorOf(anchor),
        verdict,
        // Immer mit, weil `false` hier eine Aussage ist: „ohne Rückfrage
        // geschrieben" und nicht „danach hat niemand gefragt".
        force,
      }),
    );
  },

  async withdrawSiteVerdict(anchor: SiteAnchor): Promise<number> {
    return changedCountOf(await request("DELETE", "/api/finding-sites/verdict", anchorOf(anchor)));
  },

  async requestBlindReview(revisionId: number): Promise<void> {
    await request("POST", `/api/revisions/${revisionId}/blind-review`, {});
  },

  async withdrawBlindReview(revisionId: number): Promise<void> {
    await request("DELETE", `/api/revisions/${revisionId}/blind-review`);
  },

  async revision(id: number): Promise<RevisionDetail> {
    const body = await request("GET", `/api/revisions/${id}`);

    return {
      revision: toRevision(fieldOf(body, "revision")),
      previous: toNeighbour(body, "previous"),
      next: toNeighbour(body, "next"),
      fileChanges: listOf(body, "file_changes").map(toFileChangeSummary),
      messageParts: listOf(body, "message_parts").map(toMessagePart),
    };
  },

  async fileChange(id: number): Promise<FileChangeDetail> {
    const body = await request("GET", `/api/changes/${id}`);

    return toFileChangeDetail(fieldOf(body, "change"), listOf(body, "hunks"), contentOf(body));
  },

  async findings(revisionId: number): Promise<RevisionFindings> {
    const body = await request("GET", `/api/revisions/${revisionId}/findings`);
    const fields = fieldsOf(body);
    const submitted = fields?.["submitted"];
    const blind = fields?.["blind"];
    const blindRequested = fields?.["blind_requested"];

    if (
      typeof submitted !== "boolean" ||
      typeof blind !== "boolean" ||
      typeof blindRequested !== "boolean"
    ) {
      throw new ApiError("Die Antwort zu den Befunden hat nicht die erwartete Form.");
    }

    return {
      findings: listOf(body, "findings").map(toRevisionFinding),
      excerpts: listOf(body, "excerpts").map(toSiteExcerpt),
      followUpMarks: listOf(body, "follow_up_marks").map(toFollowUpTally),
      submitted,
      blind,
      blindRequested,
    };
  },

  async addFollowUpMark(findingId: number, reason: string): Promise<void> {
    await request("POST", `/api/findings/${findingId}/follow-up-marks`, { reason });
  },

  async withdrawFollowUpMark(findingId: number): Promise<void> {
    await request("DELETE", `/api/findings/${findingId}/follow-up-marks/mine`);
  },

  async followUpMarks(query: FollowUpMarkQuery): Promise<FollowUpMarks> {
    const asked = new URLSearchParams({
      sort: query.sort,
      direction: query.descending ? "desc" : "asc",
    });

    if (query.markedBy !== null) {
      asked.set("marked_by", query.markedBy);
    }

    const body = await request("GET", `/api/follow-up-marks?${asked.toString()}`);
    const total = fieldsOf(body)?.["total"];

    if (typeof total !== "number") {
      throw new ApiError("Die Antwort zu den Vermerken hat nicht die erwartete Form.");
    }

    return {
      marks: listOf(body, "marks").map(toFollowUpMark),
      total,
      months: listOf(body, "months").map(toFollowUpMonth),
    };
  },

  async createFinding(revisionId: number, draft: FindingDraft): Promise<Finding> {
    return toFinding(
      fieldOf(
        await request("POST", `/api/revisions/${revisionId}/findings`, sentFinding(draft)),
        "finding",
      ),
    );
  },

  async updateFinding(
    revisionId: number,
    findingId: number,
    qualification: FindingQualification,
  ): Promise<Finding> {
    return toFinding(
      fieldOf(
        await request("PUT", `/api/revisions/${revisionId}/findings/${findingId}`, {
          kind: qualification.kind,
          severity: qualification.severity,
          body: qualification.body,
          title: qualification.title,
        }),
        "finding",
      ),
    );
  },

  async deleteFinding(revisionId: number, findingId: number): Promise<void> {
    await request("DELETE", `/api/revisions/${revisionId}/findings/${findingId}`);
  },

  async submit(revisionId: number): Promise<Submission> {
    return toSubmission(
      fieldOf(await request("POST", `/api/revisions/${revisionId}/submission`, {}), "submission"),
    );
  },

  async comparison(revisionId: number): Promise<Comparison> {
    const body = await request("GET", `/api/revisions/${revisionId}/comparison`);
    const fields = fieldsOf(body);
    const submitted = fields === null ? null : fields["submitted"];
    const submittedAt = fields === null ? null : fields["submitted_at"];

    if (typeof submitted !== "boolean" || !(typeof submittedAt === "string" || submittedAt === null)) {
      throw new ApiError("Die Antwort zur Gegenüberstellung hat nicht die erwartete Form.");
    }

    return {
      submitted,
      submittedAt,
      agents: listOf(body, "agents").map(toComparisonAgent),
      control: toControlComparison(fieldOf(body, "control")),
      followUpMarks: listOf(body, "follow_up_marks").map(toFollowUpTally),
    };
  },

  async monitoredProjects(): Promise<ProjectOption[]> {
    return listOf(await request("GET", "/api/projects"), "projects").map(toProjectOption);
  },

  async inventoryFiles(projectId: number, query: InventoryQuery): Promise<InventoryFiles> {
    const body = await request(
      "GET",
      `/api/projects/${projectId}/inventory-files?${asInventoryQuery(query)}`,
    );

    return {
      files: listOf(body, "files").map(toInventoryFile),
      ...pageOf(body, "der Dateiliste des Bestands"),
      patterns: listOf(body, "patterns").map(toControlPatternOption),
    };
  },

  async inventoryFindings(projectId: number, query: InventoryQuery): Promise<InventoryFindings> {
    const body = await request(
      "GET",
      `/api/projects/${projectId}/inventory-findings?${asInventoryQuery(query)}`,
    );

    return {
      findings: listOf(body, "findings").map(toFinding),
      ...pageOf(body, "den Bestandsbefunden"),
    };
  },

  async projectFile(projectId: number, path: string, revision: number): Promise<ProjectFile> {
    const asked = new URLSearchParams({ path, revision: String(revision) });

    return toProjectFile(
      fieldOf(await request("GET", `/api/projects/${projectId}/file?${asked.toString()}`), "file"),
    );
  },

  async deviations(): Promise<Deviation[]> {
    return listOf(await request("GET", "/api/deviation"), "deviations").map(toDeviation);
  },

  async completion(): Promise<Completion> {
    return toCompletion(fieldOf(await request("GET", "/api/completion"), "completion"));
  },

  async modelCall(findingId: number): Promise<ModelCall> {
    return toModelCall(
      fieldOf(await request("GET", `/api/admin/findings/${findingId}/model-call`), "model_call"),
    );
  },

  async stalledJobs(): Promise<StalledJob[]> {
    return listOf(await request("GET", "/api/admin/review-jobs"), "review_jobs").map(toStalledJob);
  },

  async requeueJob(id: number): Promise<void> {
    await request("POST", `/api/admin/review-jobs/${id}/requeue`);
  },

  async requeueJobsWithError(reason: string): Promise<RequeueTally> {
    return toRequeueTally(await request("POST", "/api/admin/review-jobs/requeue", { reason }));
  },

  async queueLoad(): Promise<QueueLoad[]> {
    return listOf(await request("GET", "/api/admin/queue-load"), "queues").map(toQueueLoad);
  },

  async endpoints(): Promise<Endpoint[]> {
    return listOf(await request("GET", "/api/admin/endpoints"), "endpoints").map(toEndpoint);
  },

  async createEndpoint(draft: EndpointDraft): Promise<Endpoint> {
    return toEndpoint(fieldOf(await request("POST", "/api/admin/endpoints", sent(draft)), "endpoint"));
  },

  async updateEndpoint(id: number, draft: EndpointDraft): Promise<Endpoint> {
    return toEndpoint(
      fieldOf(await request("PUT", `/api/admin/endpoints/${id}`, sent(draft)), "endpoint"),
    );
  },

  async endpointModels(id: number): Promise<EndpointModels> {
    const body = await request("GET", `/api/admin/endpoints/${id}/models`);

    return {
      presets: listOf(body, "presets").map(toPreset),
      baseModels: listOf(body, "base_models").map(toBaseModel),
    };
  },

  async baseModels(): Promise<BaseModel[]> {
    return listOf(await request("GET", "/api/admin/base-models"), "base_models").map(toBaseModel);
  },

  async sharpenBaseModel(
    id: number,
    slots: number,
    contextWindow: number | null,
  ): Promise<BaseModel> {
    return toBaseModel(
      fieldOf(
        await request("PUT", `/api/admin/base-models/${id}`, {
          slots,
          context_window: contextWindow,
        }),
        "base_model",
      ),
    );
  },

  async agents(): Promise<Agent[]> {
    return listOf(await request("GET", "/api/admin/agents"), "agents").map(toAgent);
  },

  async createAgent(draft: AgentDraft): Promise<Agent> {
    return toAgent(fieldOf(await request("POST", "/api/admin/agents", sentAgent(draft)), "agent"));
  },

  async updateAgent(id: number, draft: AgentDraft): Promise<Agent> {
    return toAgent(
      fieldOf(await request("PUT", `/api/admin/agents/${id}`, sentAgent(draft)), "agent"),
    );
  },

  async releaseAgent(id: number): Promise<Agent> {
    return toAgent(fieldOf(await request("POST", `/api/admin/agents/${id}/release`), "agent"));
  },

  async backfillAgent(id: number, fromRevision: number): Promise<number> {
    const body = fieldsOf(
      await request("POST", `/api/admin/agents/${id}/backfill`, { from_revision: fromRevision }),
    );
    const enqueued = body === null ? null : body["enqueued"];

    if (typeof enqueued !== "number") {
      throw new ApiError("Die Antwort zum Nachholen nennt nicht, wie viele Prüfaufträge entstanden sind.");
    }

    return enqueued;
  },

  async deleteAgent(id: number): Promise<void> {
    await request("DELETE", `/api/admin/agents/${id}`);
  },

  async projects(): Promise<ProjectAdministration> {
    return { projects: listOf(await request("GET", "/api/admin/projects"), "projects").map(toProject) };
  },

  async createProject(draft: ProjectDraft): Promise<Project> {
    return toProject(
      fieldOf(await request("POST", "/api/admin/projects", sentProject(draft)), "project"),
    );
  },

  async updateProject(id: number, draft: ProjectDraft): Promise<Project> {
    return toProject(
      fieldOf(await request("PUT", `/api/admin/projects/${id}`, sentProject(draft)), "project"),
    );
  },

  async pauseProject(id: number): Promise<Project> {
    return toProject(fieldOf(await request("POST", `/api/admin/projects/${id}/pause`), "project"));
  },

  async resumeProject(id: number): Promise<Project> {
    return toProject(fieldOf(await request("POST", `/api/admin/projects/${id}/resume`), "project"));
  },

  async startInventoryRun(id: number): Promise<InventoryRun> {
    return toInventoryRun(
      fieldOf(await request("POST", `/api/admin/projects/${id}/inventory-run`), "inventory_run"),
    );
  },

  async issueReferenceRules(): Promise<IssueTrackerAdministration> {
    const body = await request("GET", "/api/admin/issue-reference-rules");
    const address = fieldsOf(body)?.["forgejo_base_address"];

    if (!(typeof address === "string" || address === null || address === undefined)) {
      throw new ApiError("Die Antwort zum Ticketsystem hat nicht die erwartete Form.");
    }

    return {
      rules: listOf(body, "issue_reference_rules").map(toIssueReferenceRule),
      forgejoBaseAddress: address ?? null,
    };
  },

  async createIssueReferenceRule(draft: IssueReferenceRuleDraft): Promise<IssueReferenceRule> {
    return toIssueReferenceRule(
      fieldOf(
        await request("POST", "/api/admin/issue-reference-rules", sentIssueReferenceRule(draft)),
        "issue_reference_rule",
      ),
    );
  },

  async updateIssueReferenceRule(
    id: number,
    draft: IssueReferenceRuleDraft,
  ): Promise<IssueReferenceRule> {
    return toIssueReferenceRule(
      fieldOf(
        await request(
          "PUT",
          `/api/admin/issue-reference-rules/${id}`,
          sentIssueReferenceRule(draft),
        ),
        "issue_reference_rule",
      ),
    );
  },

  async deleteIssueReferenceRule(id: number): Promise<void> {
    await request("DELETE", `/api/admin/issue-reference-rules/${id}`);
  },

  async controlPatterns(): Promise<ControlPatternAdministration> {
    const body = await request("GET", "/api/admin/control-patterns");

    return {
      patterns: listOf(body, "control_patterns").map(toControlPattern),
      kinds: namesOf(body, "kinds"),
      severities: namesOf(body, "severities"),
    };
  },

  async controlPatternDraft(findingId: number): Promise<PatternDraft> {
    const body = await request("GET", `/api/admin/findings/${findingId}/control-pattern-draft`);
    const draft = fieldOf(body, "control_pattern_draft");
    const finding = toFindingAnchor(draft["finding"]);
    const fragment = draft["fragment"];
    const positiveExample = draft["positive_example"];

    if (finding === null || typeof fragment !== "string" || typeof positiveExample !== "string") {
      throw new ApiError("Die Antwort zum Entwurf eines Kontrollmusters hat nicht die erwartete Form.");
    }

    return {
      finding,
      fragment,
      positiveExample,
      kinds: namesOf(body, "kinds"),
      severities: namesOf(body, "severities"),
    };
  },

  async createControlPattern(draft: NewControlPattern): Promise<ControlPattern> {
    return toControlPattern(
      fieldOf(
        await request("POST", "/api/admin/control-patterns", {
          identifier: draft.identifier,
          fragment: draft.fragment,
          positive_example: draft.positiveExample,
          negative_example: draft.negativeExample,
          label: draft.label,
          message: draft.message,
          kind: draft.kind,
          severity: draft.severity,
          // Nur wo einer dahintersteht: Ein Feld, das die Kennung eines Befundes
          // nimmt, bekommt keine leere Zeichenkette als „keiner".
          ...(draft.originFindingId === undefined
            ? {}
            : { origin_finding_id: draft.originFindingId }),
        }),
        "control_pattern",
      ),
    );
  },

  async updateControlPattern(id: number, draft: ControlPatternDraft): Promise<ControlPattern> {
    return toControlPattern(
      fieldOf(
        await request("PUT", `/api/admin/control-patterns/${id}`, {
          label: draft.label,
          message: draft.message,
          kind: draft.kind,
          severity: draft.severity,
          // Ein Feld, das der Dialog nicht führt, geht als leer hinaus und nicht
          // als „unverändert": Ein Sniffmuster hat kein Fragment, und ein
          // weggenommenes Beispielpaar ist weggenommen.
          fragment: draft.fragment ?? "",
          positive_example: draft.positiveExample ?? "",
          negative_example: draft.negativeExample ?? "",
        }),
        "control_pattern",
      ),
    );
  },

  async retireControlPattern(id: number, reason: string): Promise<ControlPattern> {
    return toControlPattern(
      fieldOf(
        await request("POST", `/api/admin/control-patterns/${id}/retire`, { reason }),
        "control_pattern",
      ),
    );
  },

  async reinstateControlPattern(id: number): Promise<ControlPattern> {
    return toControlPattern(
      fieldOf(await request("POST", `/api/admin/control-patterns/${id}/reinstate`), "control_pattern"),
    );
  },

  async people(): Promise<PeopleAdministration> {
    const body = await request("GET", "/api/admin/people");

    return {
      people: listOf(body, "people").map(toPerson),
      mattermostConfigured: fieldsOf(body)?.["mattermost_configured"] === true,
    };
  },

  async setRole(id: number, role: Role): Promise<void> {
    await request("PUT", `/api/admin/people/${id}`, { role });
  },

  async setBlindReview(id: number, blindReview: boolean): Promise<void> {
    await request("PUT", `/api/admin/people/${id}/blind-review`, { blind_review: blindReview });
  },

  async setChatHandle(id: number, chatHandle: string | null): Promise<void> {
    await request("PUT", `/api/admin/people/${id}/chat-handle`, { chat_handle: chatHandle });
  },

  async svnIdentities(): Promise<SvnIdentity[]> {
    return listOf(await request("GET", "/api/admin/svn-identities"), "svn_identities").map(
      toSvnIdentity,
    );
  },

  async assignSvnIdentity(id: number, personId: number | null): Promise<void> {
    await request("PUT", `/api/admin/svn-identities/${id}`, { person_id: personId });
  },
};

/**
 * Die Felder auf dem Draht sind englisch und mit Unterstrich geschrieben
 * (ADR-0009); in der Oberfläche stehen sie gefügt. Übersetzt wird an dieser einen
 * Stelle.
 */
function sent(draft: EndpointDraft): Record<string, unknown> {
  return {
    name: draft.name,
    address: draft.address,
    // Ein Dialog, der das Token nie zu sehen bekommt, schickt es auch nicht
    // zurück — dann bleibt das hinterlegte stehen.
    ...(draft.token === null ? {} : { token: draft.token }),
    max_parallel_calls: draft.maxParallelCalls,
  };
}

/**
 * Die Frage an den Strom als Abfragezeichenkette. Ein Filter, nach dem nicht
 * gefiltert wird, steht gar nicht darin — ein leerer Wert wäre für den Server
 * etwas anderes als ein fehlender.
 */
function asQuery(query: RevisionStreamQuery): string {
  const parameters = new URLSearchParams({
    page: String(query.page),
    per_page: String(query.perPage),
  });

  if (query.projectId !== null) {
    parameters.set("project_id", String(query.projectId));
  }

  if (query.svnIdentity !== null) {
    parameters.set("svn_identity", query.svnIdentity);
  }

  if (query.reviewRecommended !== null) {
    parameters.set("review_recommended", String(query.reviewRecommended));
  }

  if (query.state !== null) {
    parameters.set("state", query.state);
  }

  if (query.submitted !== null) {
    parameters.set("submitted", String(query.submitted));
  }

  if (query.severity !== null) {
    parameters.set("severity", query.severity);
  }

  if (query.hasFindings !== null) {
    parameters.set("has_findings", String(query.hasFindings));
  }

  if (query.committer !== null) {
    parameters.set("committer", query.committer);
  }

  return parameters.toString();
}

/**
 * Die Frage an den Befundvorrat als Abfragezeichenkette. Ein Filter, nach dem
 * nicht gefiltert wird, steht gar nicht darin — ein leerer Wert wäre für den
 * Server etwas anderes als ein fehlender.
 */
function asSiteQuery(query: SiteQuery): string {
  const parameters = new URLSearchParams({
    page: String(query.page),
    per_page: String(query.perPage),
  });

  if (query.projectId !== null) {
    parameters.set("project_id", String(query.projectId));
  }

  if (query.path !== null) {
    parameters.set("path", query.path);
  }

  if (query.kind !== null) {
    parameters.set("kind", query.kind);
  }

  if (query.severity !== null) {
    parameters.set("severity", query.severity);
  }

  if (query.status !== null) {
    parameters.set("status", query.status);
  }

  if (query.qualification !== null) {
    parameters.set("qualification", query.qualification);
  }

  if (query.origin !== null) {
    parameters.set("origin", query.origin);
  }

  if (query.from !== null) {
    parameters.set("from", query.from);
  }

  if (query.to !== null) {
    parameters.set("to", query.to);
  }

  if (query.committer !== null) {
    parameters.set("committer", query.committer);
  }

  // Dieser eine geht immer mit: Ohne die Angabe wirkt im Server keine
  // Anzeigeschwelle, und „aus" ist hier eine Aussage und kein fehlender Wert.
  parameters.set("thresholds", String(query.thresholds));

  return parameters.toString();
}

/**
 * Der **Anker** einer Stelle als Abfragezeichenkette — Projekt, Pfad, Revision,
 * Zeilenbereich (ADR-0006).
 *
 * Jedes Feld steht darin: Ein fehlender Filter hieße „danach wird nicht
 * gefiltert", ein fehlender Anker hieße gar nichts.
 */
function changedCountOf(body: unknown): number {
  const findings = fieldsOf(body)?.["findings"];

  if (typeof findings !== "number") {
    throw new ApiError("Die Antwort nennt nicht, an wie vielen Befunden der Handgriff gewirkt hat.");
  }

  return findings;
}

/**
 * Der Anker einer Stelle im Rumpf einer Anfrage — der Weg, den alles nimmt, was
 * etwas ändert. Lesend steht derselbe Anker in der Abfragezeichenkette.
 */
/**
 * Der Zuschnitt als Abfragezeichenkette. Ohne Anker steht nur die Revision
 * darin — der Server liest daran, welcher Zuschnitt gemeint ist.
 */
function asCut(cut: DeliveryCut): string {
  const parameters = new URLSearchParams({ revision_id: String(cut.revisionId) });

  if (cut.anchor !== null) {
    for (const [name, value] of Object.entries(anchorOf(cut.anchor))) {
      parameters.set(name, String(value));
    }
  }

  return parameters.toString();
}

/**
 * Die Auskunft des Zustelldialogs.
 *
 * Fehlt das Feld `obstacle`, ist das eine unerwartete Form und kein stilles
 * „es gibt kein Hindernis": Ein Dialog, der den Grund nicht kennt, ließe einen
 * fehlenden Knopf unerklärt dastehen.
 */
function toDeliveryPreparation(body: unknown): DeliveryPreparation {
  const fields = fieldsOf(body);

  if (fields === null || !("obstacle" in fields)) {
    throw new ApiError("Die Antwort zur Zustellung hat nicht die erwartete Form.");
  }

  return {
    targets: listOf(body, "targets").map(toDeliveryTarget),
    obstacle: typeof fields["obstacle"] === "string" ? fields["obstacle"] : null,
    deliveredAt: typeof fields["delivered_at"] === "string" ? fields["delivered_at"] : null,
    chat: toDeliveryChat(fields["chat"]),
  };
}

/**
 * Der Chatteil der Auskunft.
 *
 * Er muss dastehen: Fehlte er, wüsste der Dialog nicht, ob es diesen Weg nicht
 * gibt oder ob die Antwort von einem älteren Server kommt — und zeigte im Zweifel
 * einen Knopf, der ins Leere greift.
 */
function toDeliveryChat(body: unknown): DeliveryChat {
  const fields = fieldsOf(body);

  if (fields === null || !("suggested" in fields) || !("obstacle" in fields)) {
    throw new ApiError("Die Antwort zur Direktnachricht hat nicht die erwartete Form.");
  }

  return {
    recipients: listOf(body, "recipients").map(toChatRecipient),
    suggested: typeof fields["suggested"] === "number" ? fields["suggested"] : null,
    obstacle: typeof fields["obstacle"] === "string" ? fields["obstacle"] : null,
  };
}

function toChatRecipient(fields: Record<string, unknown>): ChatRecipient {
  const personId = fields["person_id"];
  const displayName = fields["display_name"];

  if (typeof personId !== "number" || typeof displayName !== "string") {
    throw new ApiError("Ein Empfänger der Direktnachricht hat nicht die erwartete Form.");
  }

  return { personId, displayName };
}

function toDeliveryTarget(fields: Record<string, unknown>): DeliveryTarget {
  const { owner, repo, number, target, title, open, address } = fields;

  if (
    typeof owner !== "string" ||
    typeof repo !== "string" ||
    typeof number !== "number" ||
    typeof target !== "string" ||
    typeof title !== "string" ||
    typeof open !== "boolean" ||
    typeof address !== "string"
  ) {
    throw new ApiError("Ein Ziel der Zustellung hat nicht die erwartete Form.");
  }

  return { owner, repo, number, target, title, open, address };
}

/**
 * Was zurückkommt, nachdem der Text draußen ist.
 *
 * Ohne Ziel und Zeitpunkt wäre die Bestätigung eine Behauptung: Die Ansicht
 * schriebe „zugestellt", ohne sagen zu können, wohin.
 */
function toDelivered(body: unknown): Delivered {
  const fields = fieldsOf(fieldsOf(body)?.["delivery"]);
  const target = fields?.["target"];
  const at = fields?.["at"];
  const text = fields?.["text"];

  if (typeof target !== "string" || typeof at !== "string" || typeof text !== "string") {
    throw new ApiError("Die Antwort der Zustellung nennt nicht, was hinausgegangen ist.");
  }

  return { target, at, text };
}

function anchorOf(anchor: SiteAnchor): Record<string, number | string> {
  return {
    project_id: anchor.projectId,
    path: anchor.path,
    revision_number: anchor.revisionNumber,
    start_line: anchor.startLine,
    end_line: anchor.endLine,
  };
}

function asAnchor(site: Site): string {
  return new URLSearchParams({
    project_id: String(site.projectId),
    path: site.path,
    revision_number: String(site.revisionNumber),
    start_line: String(site.startLine),
    end_line: String(site.endLine),
  }).toString();
}

/**
 * Der Text einer Übergabe aus der Antwort — für beide Zuschnitte derselbe
 * Weg, denn beide antworten in derselben Form.
 *
 * Eine Antwort ohne Text ist ein Fehler und keine leere Übergabe: Der Dialog
 * zeigte sonst ein leeres Feld und behauptete, das sei der Text.
 */
function handoffTextOf(body: unknown): string {
  const text = fieldsOf(body)?.["text"];

  if (typeof text !== "string") {
    throw new ApiError("Die Antwort der Übergabe trägt keinen Text.");
  }

  return text;
}

/**
 * Die Frage an den Bestand als Abfragezeichenkette. Ein Filter, nach dem nicht
 * gefiltert wird, steht gar nicht darin — ein leerer Wert wäre für den Server
 * etwas anderes als ein fehlender.
 */
function asInventoryQuery(query: InventoryQuery): string {
  const parameters = new URLSearchParams({
    page: String(query.page),
    per_page: String(query.perPage),
  });

  if (query.pattern !== null) {
    parameters.set("pattern", query.pattern);
  }

  if (query.severity !== null) {
    parameters.set("severity", query.severity);
  }

  if (query.status !== null) {
    parameters.set("status", query.status);
  }

  if (query.qualification !== null) {
    parameters.set("qualification", query.qualification);
  }

  if (query.path !== null) {
    parameters.set("path", query.path);
  }

  return parameters.toString();
}

function sentFinding(draft: FindingDraft): Record<string, unknown> {
  return {
    file_change_id: draft.fileChangeId,
    revision_number: draft.revisionNumber,
    start_line: draft.startLine,
    end_line: draft.endLine,
    kind: draft.kind,
    severity: draft.severity,
    body: draft.body,
    title: draft.title,
  };
}

function sentProject(draft: ProjectDraft): Record<string, unknown> {
  return {
    name: draft.name,
    svn_path: draft.svnPath,
    current_php_version: draft.currentPhpVersion,
    planned_php_versions: draft.plannedPhpVersions,
    start_revision: draft.startRevision,
    display_threshold: draft.displayThreshold,
    // Eine Bestätigung, die niemand gegeben hat, wird auch nicht mitgeschickt.
    ...(draft.resetProgress === undefined ? {} : { reset_progress: draft.resetProgress }),
  };
}

function sentAgent(draft: AgentDraft): Record<string, unknown> {
  return {
    name: draft.name,
    endpoint_id: draft.endpointId,
    preset_identifier: draft.presetIdentifier,
    parameters: draft.parameters,
    context_limit: draft.contextLimit,
    review_prompt_template: draft.reviewPromptTemplate,
    synthesis_prompt_template: draft.synthesisPromptTemplate,
    refutation_enabled: draft.refutationEnabled,
    synthesis_agent: draft.synthesisAgent,
    reasoning: draft.reasoning,
    ...(draft.state === undefined ? {} : { state: draft.state }),
  };
}

async function request(method: string, path: string, body?: unknown): Promise<unknown> {
  let response: Response;

  try {
    response = await fetch(withBase(path), {
      method,
      headers:
        body === undefined
          ? { Accept: "application/json" }
          : { Accept: "application/json", "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    throw new ApiError(`Die Anwendung ist nicht erreichbar: ${describe(error)}`, "not_connected");
  }

  if (response.status === 204) {
    return null;
  }

  let payload: unknown;

  try {
    payload = await response.json();
  } catch {
    if (!response.ok) {
      throw new ApiError(`${path} antwortete mit ${response.status}.`);
    }

    throw new ApiError(`${path} antwortete nicht mit JSON.`);
  }

  if (!response.ok) {
    throw errorOf(payload, path, response.status);
  }

  return payload;
}

function errorOf(payload: unknown, path: string, status: number): ApiError {
  const fields = fieldsOf(payload);
  const message = fields === null ? null : fields["error"];
  const code = fields === null ? null : fields["code"];
  const text = typeof message === "string" ? message : `${path} antwortete mit ${status}.`;

  // Der eine Fall, der mehr trägt als Wortlaut und Kennung: Ein fremdes
  // „behoben" würde überschrieben, und die Rückfrage nennt Person und Zeitpunkt
  // (ADR-0022). Sie hier abzuholen ist der einzige Weg, auf dem sie die Fläche
  // erreichen — hinter `request()` ist von der Antwort nichts mehr übrig.
  if (code === FOREIGN_FIX && fields !== null) {
    const person = fields["fixed_by"];
    const at = fields["fixed_at"];

    if (typeof person === "string") {
      return new ForeignFixError(text, person, typeof at === "string" ? at : null);
    }
  }

  return new ApiError(text, typeof code === "string" ? code : null);
}

/**
 * Die Gerätekennung: eine stabile Kennung dieses Browsers auf diesem Gerät. Die
 * Gegenstelle führt sie an jeder Anmeldeaktion im Feld `device_id`.
 *
 * `crypto.randomUUID` gibt es nur im sicheren Kontext; im Firmennetz ohne TLS
 * ist das der Normalfall — deshalb ein Rückfall, der dasselbe leistet: eine
 * Kennung, die auf keinem zweiten Gerät entsteht.
 */
export function device(store: Storage = localStorage): string {
  const known = store.getItem(DEVICE_KEY);

  if (known !== null && known !== "") {
    return known;
  }

  const fresh =
    typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

  store.setItem(DEVICE_KEY, fresh);

  return fresh;
}

const DEVICE_KEY = "devtools.device";

function toScaffoldStatus(body: unknown): ScaffoldStatus {
  const fields = fieldsOf(body);

  if (fields === null) {
    throw new ApiError("Die Statusroute antwortete nicht mit einem Objekt.");
  }

  const application = fields["application"];
  const version = fields["version"];
  const schemaVersion = fields["schema_version"];
  const journalMode = fields["journal_mode"];
  const timestamp = fields["timestamp"];

  if (
    typeof application !== "string" ||
    typeof version !== "string" ||
    typeof schemaVersion !== "number" ||
    typeof journalMode !== "string" ||
    typeof timestamp !== "string"
  ) {
    throw new ApiError("Die Antwort der Statusroute hat nicht die erwartete Form.");
  }

  return { application, version, schemaVersion, journalMode, timestamp };
}

function toVersionHistory(body: unknown): VersionHistory {
  const fields = fieldsOf(body);
  const running = fields === null ? undefined : fields["running"];
  const address = fields === null ? undefined : fields["issue_base_address"];

  if (!(typeof running === "string" || running === null)) {
    throw new ApiError("Die Antwort zur Versionshistorie nennt die laufende Fassung nicht.");
  }

  // Ohne diese Angabe wüsste die Seite nicht, ob sie verlinken darf — und ein
  // geratener Verweis führte irgendwohin (ADR-0027).
  if (!(typeof address === "string" || address === null)) {
    throw new ApiError("Die Antwort zur Versionshistorie nennt die Ticketadresse nicht.");
  }

  return {
    running,
    issueBaseAddress: address,
    versions: listOf(body, "versions").map(toVersion),
  };
}

function toVersion(fields: Record<string, unknown>): Version {
  const { version, date, tickets, note } = fields;

  if (
    typeof version !== "string" ||
    typeof date !== "string" ||
    typeof tickets !== "number" ||
    !(typeof note === "string" || note === null)
  ) {
    throw new ApiError("Eine Fassung der Versionshistorie hat nicht die erwartete Form.");
  }

  return { version, date, tickets, note, entries: listOf(fields, "entries").map(toVersionEntry) };
}

function toVersionEntry(fields: Record<string, unknown>): VersionEntry {
  const { heading, markdown, tickets } = fields;

  if (
    !(typeof heading === "string" || heading === null) ||
    typeof markdown !== "string" ||
    !Array.isArray(tickets)
  ) {
    throw new ApiError("Ein Eintrag der Versionshistorie hat nicht die erwartete Form.");
  }

  return { heading, markdown, tickets: tickets.map(ticketNumberOf) };
}

/** Eine Ticketnummer aus der Antwort. Etwas, das keine Zahl ist, ist keine Nummer. */
function ticketNumberOf(value: unknown): number {
  if (typeof value !== "number") {
    throw new ApiError("Ein Eintrag der Versionshistorie nennt eine Ticketnummer, die keine ist.");
  }

  return value;
}

function toSessionState(body: unknown): SessionState {
  const fields = fieldsOf(body);
  const displayName = fields === null ? null : fields["display_name"];

  if (typeof displayName !== "string") {
    throw new ApiError("Die Antwort zur Sitzung hat nicht die erwartete Form.");
  }

  return { displayName };
}

function toCurrentPerson(body: unknown): CurrentPerson {
  const fields = fieldsOf(body);

  if (fields === null) {
    throw new ApiError("Die Antwort zur eigenen Person hat nicht die erwartete Form.");
  }

  const id = fields["id"];
  const displayName = fields["display_name"];
  const role = fields["role"];

  if (typeof id !== "number" || typeof displayName !== "string" || !isRole(role)) {
    throw new ApiError("Die Antwort zur eigenen Person hat nicht die erwartete Form.");
  }

  // Was nicht ausdrücklich „ja" sagt, gilt als „keine Kennung zugeordnet": Der
  // Filter „an meinem Code" steht dann abgeschaltet da, und das ist die harmlose
  // Seite des Irrtums — angeschaltet käme eine leere Liste heraus, die sich wie
  // ein sauberer Stand liest.
  return { id, displayName, role, hasSvnIdentity: fields["has_svn_identity"] === true };
}

/** Ein benanntes Objekt aus einer Antwort. Fehlt es, ist das ein Fehler und kein leeres. */
function fieldOf(body: unknown, name: string): Record<string, unknown> {
  const fields = fieldsOf(body);
  const value = fields === null ? null : fieldsOf(fields[name]);

  if (value === null) {
    throw new ApiError(`Die Antwort trägt kein Objekt „${name}".`);
  }

  return value;
}

/** Eine benannte Liste von Objekten aus einer Antwort. */
function listOf(body: unknown, name: string): Record<string, unknown>[] {
  const fields = fieldsOf(body);
  const value = fields === null ? null : fields[name];

  if (!Array.isArray(value)) {
    throw new ApiError(`Die Antwort trägt keine Liste „${name}".`);
  }

  return value.map((entry) => {
    const item = fieldsOf(entry);

    if (item === null) {
      throw new ApiError(`Die Liste „${name}" enthält etwas, das kein Objekt ist.`);
    }

    return item;
  });
}

function toRevisionStream(body: unknown): RevisionStream {
  const fields = fieldsOf(body);
  const page = fields === null ? null : fields["page"];
  const perPage = fields === null ? null : fields["per_page"];
  const pages = fields === null ? null : fields["pages"];
  const total = fields === null ? null : fields["total"];

  if (
    typeof page !== "number" ||
    typeof perPage !== "number" ||
    typeof pages !== "number" ||
    typeof total !== "number"
  ) {
    throw new ApiError("Die Antwort zu den Revisionen hat nicht die erwartete Form.");
  }

  return {
    revisions: listOf(body, "revisions").map(toRevisionSummary),
    page,
    perPage,
    pages,
    total,
    projects: listOf(body, "projects").map(toProjectOption),
    svnIdentities: namesOf(body, "svn_identities"),
  };
}

function toRevisionSummary(fields: Record<string, unknown>): RevisionSummary {
  const { id, number, message, state } = fields;
  const projectId = fields["project_id"];
  const projectName = fields["project_name"];
  const svnIdentity = fields["svn_identity"];
  const personName = fields["person_name"];
  const committedAt = fields["committed_at"];
  const reviewRecommended = fields["review_recommended"];
  const submitted = fields["submitted"];
  const blind = fields["blind"];
  const blindRequested = fields["blind_requested"];
  const blindPossible = fields["blind_possible"];

  if (
    typeof id !== "number" ||
    typeof projectId !== "number" ||
    typeof projectName !== "string" ||
    typeof number !== "number" ||
    typeof svnIdentity !== "string" ||
    !(typeof personName === "string" || personName === null) ||
    typeof committedAt !== "string" ||
    typeof message !== "string" ||
    !isRevisionState(state) ||
    typeof reviewRecommended !== "boolean" ||
    typeof submitted !== "boolean" ||
    typeof blind !== "boolean" ||
    typeof blindRequested !== "boolean" ||
    typeof blindPossible !== "boolean"
  ) {
    throw new ApiError("Die Antwort zu einer Revision hat nicht die erwartete Form.");
  }

  return {
    id,
    projectId,
    projectName,
    number,
    svnIdentity,
    personName,
    committedAt,
    message,
    state,
    reviewRecommended,
    submitted,
    blind,
    blindRequested,
    blindPossible,
    deliveredAt: typeof fields["delivered_at"] === "string" ? fields["delivered_at"] : null,
    issueReferences: issueReferencesOf(fields["issue_references"]),
    findings: toRevisionHeat(fields["findings"]),
  };
}

/**
 * Die **Ticketverweise** einer Listenzeile (#113).
 *
 * Fehlt das Feld, ist die Zeile ohne Verweis und nicht kaputt: Die Spalte bleibt
 * dann leer. Steht dort etwas, muss es die erwartete Form haben — ein halb
 * gelesener Verweis würde zu einem Link, der irgendwohin zeigt.
 */
function issueReferencesOf(value: unknown): IssueReference[] {
  if (value === null || value === undefined) {
    return [];
  }

  if (!Array.isArray(value)) {
    throw new ApiError("Die Ticketverweise einer Zeile haben nicht die erwartete Form.");
  }

  return value.map((entry) => {
    const fields = fieldsOf(entry);
    const label = fields?.["label"];
    const ticketList = fields?.["ticket_list"];
    const number = fields?.["number"];
    const address = fields?.["address"];

    if (
      typeof label !== "string" ||
      typeof ticketList !== "string" ||
      typeof number !== "number" ||
      typeof address !== "string"
    ) {
      throw new ApiError("Ein Ticketverweis hat nicht die erwartete Form.");
    }

    return { label, ticketList, number, address };
  });
}

/**
 * Das Wärmebild einer Zeile.
 *
 * `null` wird durchgereicht und nicht zu einem leeren Wärmebild geglättet: Es
 * heißt „nicht zu sehen", und eine Null an dieser Stelle behauptete „nichts
 * gefunden".
 */
function toRevisionHeat(value: unknown): RevisionHeat | null {
  if (value === null || value === undefined) {
    return null;
  }

  const fields = fieldsOf(value);
  const total = fields?.["total"];
  const severity = fields?.["severity"] ?? null;
  const title = fields?.["title"] ?? "";

  if (
    typeof total !== "number" ||
    typeof title !== "string" ||
    !(severity === null || isSeverity(severity))
  ) {
    throw new ApiError("Das Wärmebild einer Revision hat nicht die erwartete Form.");
  }

  return {
    total,
    severity,
    title,
    authors: authorsOf(fields?.["authors"] ?? []),
    agreement: toAgreement(fields?.["agreement"]),
  };
}

function toAgreement(value: unknown): Agreement | null {
  if (value === null || value === undefined) {
    return null;
  }

  const fields = fieldsOf(value);
  const carrying = fields?.["carrying"];
  const reviewing = fields?.["reviewing"];
  const baseModels = fields?.["base_models"];

  if (
    typeof carrying !== "number" ||
    typeof reviewing !== "number" ||
    typeof baseModels !== "number"
  ) {
    throw new ApiError("Die Übereinstimmung einer Revision hat nicht die erwartete Form.");
  }

  return { carrying, reviewing, baseModels };
}

/**
 * Eine Zeile des Befundvorrats.
 *
 * `revision_id` darf fehlen und wird dann `null`: Eine Stelle, die keine
 * Revision zeigt, hat keinen Sprung — und keinen geratenen.
 */
function toSite(fields: Record<string, unknown>): Site {
  const { path, severity, title, total } = fields;
  const lowestSeverity = fields["lowest_severity"];
  const projectId = fields["project_id"];
  const projectName = fields["project_name"];
  const revisionNumber = fields["revision_number"];
  const revisionId = fields["revision_id"];
  const startLine = fields["start_line"];
  const endLine = fields["end_line"];
  // Fehlt der gekürzte Pfad, steht der ganze da: Die Zeile bleibt lesbar, statt
  // an einer fehlenden Anzeigehilfe zu scheitern.
  const relativePath = fields["relative_path"] ?? path;

  if (
    typeof projectId !== "number" ||
    typeof projectName !== "string" ||
    typeof path !== "string" ||
    typeof relativePath !== "string" ||
    typeof revisionNumber !== "number" ||
    !(typeof revisionId === "number" || revisionId === null) ||
    typeof startLine !== "number" ||
    typeof endLine !== "number" ||
    !isSeverity(severity) ||
    !isSeverity(lowestSeverity) ||
    typeof title !== "string" ||
    typeof total !== "number"
  ) {
    throw new ApiError("Die Antwort zu einer Stelle hat nicht die erwartete Form.");
  }

  return {
    projectId,
    projectName,
    path,
    relativePath,
    revisionNumber,
    revisionId,
    startLine,
    endLine,
    severity,
    lowestSeverity,
    title,
    total,
    authors: authorsOf(fields["authors"]),
    statuses: statusesOf(fields["statuses"]),
    qualifications: qualificationsOf(fields["qualifications"]),
    qualifiedAt: typeof fields["qualified_at"] === "string" ? fields["qualified_at"] : null,
    qualifiedBy: typeof fields["qualified_by"] === "string" ? fields["qualified_by"] : "",
    kinds: kindsOf(fields["kinds"]),
    agreement: toAgreement(fields["agreement"]),
    deliveredAt: typeof fields["delivered_at"] === "string" ? fields["delivered_at"] : null,
    issueReferences: issueReferencesOf(fields["issue_references"]),
    message: typeof fields["message"] === "string" ? fields["message"] : "",
    // Wie beim Kommentar: Fehlt die Angabe, steht dort „es gibt keinen Commit"
    // und nicht ein Fehler — die Zeile bleibt lesbar, und die Spalte bleibt leer.
    svnIdentity: typeof fields["svn_identity"] === "string" ? fields["svn_identity"] : "",
    personName: typeof fields["person_name"] === "string" ? fields["person_name"] : null,
  };
}

/**
 * Die Urheber einer Stelle, jeder mit seiner Herkunft.
 *
 * Eine unbekannte Herkunft ist eine unerwartete Form und keine stille Annahme:
 * Ein Muster als Agent zu zeigen behauptete eine Meinung, wo eine Regel steht.
 */
function authorsOf(value: unknown): SiteAuthor[] {
  if (!Array.isArray(value)) {
    throw new ApiError("Die Antwort zu einer Stelle nennt ihre Urheber nicht.");
  }

  return value.map((entry) => {
    const fields = fieldsOf(entry);
    const name = fields?.["name"];
    const origin = fields?.["origin"];

    if (typeof name !== "string" || !isFindingOrigin(origin)) {
      throw new ApiError("Der Urheber einer Stelle hat nicht die erwartete Form.");
    }

    return { name, origin };
  });
}

/** Die Status einer Stelle. Ein Wert außerhalb der Menge ist eine unerwartete Form. */
function statusesOf(value: unknown): FindingStatus[] {
  if (!Array.isArray(value)) {
    throw new ApiError("Die Antwort zu einer Stelle nennt ihre Status nicht.");
  }

  return value.map((entry) => {
    if (!isFindingStatus(entry)) {
      throw new ApiError("Die Antwort zu einer Stelle nennt einen unbekannten Status.");
    }

    return entry;
  });
}

/** Die Qualifizierungen einer Stelle. Ein Wert außerhalb der Menge ist eine unerwartete Form. */
function qualificationsOf(value: unknown): Qualification[] {
  if (!Array.isArray(value)) {
    throw new ApiError("Die Antwort zu einer Stelle nennt ihre Qualifizierungen nicht.");
  }

  return value.map((entry) => {
    if (!isQualification(entry)) {
      throw new ApiError("Die Antwort zu einer Stelle nennt eine unbekannte Qualifizierung.");
    }

    return entry;
  });
}

/** Die Befundarten einer Stelle. Ein Wert außerhalb der Menge ist eine unerwartete Form. */
function kindsOf(value: unknown): FindingKind[] {
  if (!Array.isArray(value)) {
    throw new ApiError("Die Antwort zu einer Stelle nennt ihre Befundarten nicht.");
  }

  return value.map((entry) => {
    if (!isFindingKind(entry)) {
      throw new ApiError("Die Antwort zu einer Stelle nennt eine unbekannte Befundart.");
    }

    return entry;
  });
}

/** Eine Liste von Zeichenketten aus einer Antwort — die Urheber einer Stelle. */
function textsOf(value: unknown, what: string): string[] {
  if (!Array.isArray(value)) {
    throw new ApiError(`Die Antwort nennt die ${what} nicht.`);
  }

  return value.map((entry) => {
    if (typeof entry !== "string") {
      throw new ApiError(`Die ${what} enthalten etwas, das keine Zeichenkette ist.`);
    }

    return entry;
  });
}

function toRevision(fields: Record<string, unknown>): Revision {
  const { id, number, message, state } = fields;
  const projectId = fields["project_id"];
  const svnIdentity = fields["svn_identity"];
  const committedAt = fields["committed_at"];

  if (
    typeof id !== "number" ||
    typeof projectId !== "number" ||
    typeof number !== "number" ||
    typeof svnIdentity !== "string" ||
    typeof committedAt !== "string" ||
    typeof message !== "string" ||
    !isRevisionState(state)
  ) {
    throw new ApiError("Die Antwort zu einer Revision hat nicht die erwartete Form.");
  }

  return { id, projectId, number, svnIdentity, committedAt, message, state };
}

/**
 * Ein Nachbar der Revision, oder `null`.
 *
 * `null` ist hier ein Wert und kein Fehler: Die erste Revision eines Projekts
 * hat keinen Vorgänger. Was dagegen dasteht und nicht die erwartete Form hat,
 * wird zum Fehler — die Oberfläche zeigt „nicht verbunden" und erfindet keinen
 * Weg, der ins Leere führte.
 */
function toNeighbour(body: unknown, name: string): RevisionNeighbour | null {
  const fields = fieldsOf(body);
  const value = fields === null ? null : fields[name];

  if (value === null || value === undefined) {
    return null;
  }

  const neighbour = fieldsOf(value);
  const id = neighbour === null ? null : neighbour["id"];
  const number = neighbour === null ? null : neighbour["number"];

  if (typeof id !== "number" || typeof number !== "number") {
    throw new ApiError(`Die Antwort trägt unter „${name}" keine Revision.`);
  }

  return { id, number };
}

/**
 * Ein Abschnitt des Commit-Kommentars.
 *
 * Eine fehlende Adresse ist hier ein Wert und kein Fehler: Der weitaus meiste
 * Text eines Kommentars ist keiner. Was dagegen dasteht und nicht die erwartete
 * Form hat, wird zum Fehler — ein halb gelesener Kommentar wäre schlimmer als
 * gar keiner.
 */
function toMessagePart(fields: Record<string, unknown>): MessagePart {
  const { text, address } = fields;

  if (typeof text !== "string" || !(typeof address === "string" || address === null)) {
    throw new ApiError("Die Antwort zum Commit-Kommentar hat nicht die erwartete Form.");
  }

  return { text, address };
}

function toFileChangeSummary(fields: Record<string, unknown>): FileChangeSummary {
  const { id, path, summary } = fields;
  const changeAction = fields["change_action"];
  const previousRevision = fields["previous_revision"];
  const skippedReason = fields["skipped_reason"];
  const agentSkips = fields["agent_skips"];

  if (
    typeof id !== "number" ||
    typeof path !== "string" ||
    !isChangeAction(changeAction) ||
    !(typeof previousRevision === "number" || previousRevision === null) ||
    !(typeof summary === "string" || summary === null) ||
    !(typeof skippedReason === "string" || skippedReason === null) ||
    !Array.isArray(agentSkips)
  ) {
    throw new ApiError("Die Antwort zu einer Dateiänderung hat nicht die erwartete Form.");
  }

  return {
    id,
    path,
    changeAction,
    previousRevision,
    summary,
    skippedReason,
    agentSkips: agentSkips.map(toAgentSkip),
  };
}

function toAgentSkip(entry: unknown): AgentSkip {
  const fields = fieldsOf(entry);
  const agentId = fields === null ? undefined : fields["agent_id"];
  const agentName = fields === null ? undefined : fields["agent_name"];
  const reason = fields === null ? undefined : fields["reason"];

  if (typeof agentId !== "number" || typeof agentName !== "string" || typeof reason !== "string") {
    throw new ApiError("Die Antwort zu einem übersprungenen Prüfauftrag hat nicht die erwartete Form.");
  }

  return { agentId, agentName, reason };
}

function toFileChangeDetail(
  fields: Record<string, unknown>,
  hunks: Record<string, unknown>[],
  content: string | null,
): FileChangeDetail {
  const revisionId = fields["revision_id"];
  const projectId = fields["project_id"];
  const revisionNumber = fields["revision_number"];
  const contentRevision = fields["content_revision"];
  const sourceEncoding = fields["source_encoding"];

  if (
    typeof revisionId !== "number" ||
    typeof projectId !== "number" ||
    typeof revisionNumber !== "number" ||
    typeof contentRevision !== "number" ||
    !(typeof sourceEncoding === "string" || sourceEncoding === null)
  ) {
    throw new ApiError("Die Antwort zu einer Dateiänderung hat nicht die erwartete Form.");
  }

  return {
    ...toFileChangeSummary(fields),
    revisionId,
    projectId,
    revisionNumber,
    contentRevision,
    sourceEncoding,
    hunks: hunks.map(toDiffHunk),
    content,
  };
}

/** Der vollständige Dateiinhalt. `null` heißt: Es gibt keinen — nicht „leer". */
function contentOf(body: unknown): string | null {
  const fields = fieldsOf(body);
  const content = fields === null ? undefined : fields["content"];

  if (!(typeof content === "string" || content === null)) {
    throw new ApiError("Die Antwort zu einer Dateiänderung trägt keinen Dateiinhalt.");
  }

  return content;
}

function toDiffHunk(fields: Record<string, unknown>): DiffHunk {
  const { heading, lines } = fields;
  const oldStart = fields["old_start"];
  const oldCount = fields["old_count"];
  const newStart = fields["new_start"];
  const newCount = fields["new_count"];

  if (
    typeof oldStart !== "number" ||
    typeof oldCount !== "number" ||
    typeof newStart !== "number" ||
    typeof newCount !== "number" ||
    typeof heading !== "string" ||
    !Array.isArray(lines)
  ) {
    throw new ApiError("Die Antwort zu einem Hunk hat nicht die erwartete Form.");
  }

  return {
    oldStart,
    oldCount,
    newStart,
    newCount,
    heading,
    lines: lines.map((line) => {
      const item = fieldsOf(line);

      if (item === null) {
        throw new ApiError("Ein Hunk enthält etwas, das keine Zeile ist.");
      }

      return toDiffLine(item);
    }),
  };
}

function toDiffLine(fields: Record<string, unknown>): DiffLine {
  const { kind, text } = fields;
  const oldLine = fields["old_line"];
  const newLine = fields["new_line"];

  if (
    !isDiffLineKind(kind) ||
    !(typeof oldLine === "number" || oldLine === null) ||
    !(typeof newLine === "number" || newLine === null) ||
    typeof text !== "string"
  ) {
    throw new ApiError("Die Antwort zu einer Diff-Zeile hat nicht die erwartete Form.");
  }

  return { kind, oldLine, newLine, text };
}

function toFinding(fields: Record<string, unknown>): Finding {
  const { id, kind, severity, body, title } = fields;
  const projectId = fields["project_id"];
  const path = fields["path"];
  const revisionNumber = fields["revision_number"];
  const startLine = fields["start_line"];
  const endLine = fields["end_line"];
  const status = fields["status"];
  const statusChangedAt = fields["status_changed_at"];
  const statusChangedBy = fields["status_changed_by"];
  const qualification = fields["qualification"] ?? null;
  const qualifiedAt = fields["qualified_at"] ?? null;
  const qualifiedBy = fields["qualified_by"] ?? "";
  const createdAt = fields["created_at"] ?? null;
  const consequence = fields["consequence"] ?? null;
  const remedy = fields["remedy"] ?? null;

  if (
    typeof id !== "number" ||
    typeof projectId !== "number" ||
    typeof path !== "string" ||
    typeof revisionNumber !== "number" ||
    typeof startLine !== "number" ||
    typeof endLine !== "number" ||
    !isFindingKind(kind) ||
    !isSeverity(severity) ||
    typeof body !== "string" ||
    !(typeof consequence === "string" || consequence === null) ||
    !(typeof remedy === "string" || remedy === null) ||
    typeof title !== "string" ||
    !isFindingStatus(status) ||
    !(typeof statusChangedAt === "string" || statusChangedAt === null) ||
    !(typeof statusChangedBy === "number" || statusChangedBy === null) ||
    !(isQualification(qualification) || qualification === null) ||
    !(typeof qualifiedAt === "string" || qualifiedAt === null) ||
    typeof qualifiedBy !== "string" ||
    !(typeof createdAt === "string" || createdAt === null)
  ) {
    throw new ApiError("Die Antwort zu einem Befund hat nicht die erwartete Form.");
  }

  return {
    id,
    projectId,
    path,
    revisionNumber,
    startLine,
    endLine,
    kind,
    severity,
    body,
    consequence,
    remedy,
    title,
    status,
    statusChangedAt,
    statusChangedBy,
    qualification,
    qualifiedAt,
    qualifiedBy,
    createdAt,
  };
}

/** Ein Befund samt Urheber, wie die Befundleiste ihn liest. */
function toRevisionFinding(fields: Record<string, unknown>): RevisionFinding {
  const { origin, author, own } = fields;
  const refutations = fields["refutations"];

  if (
    !isFindingOrigin(origin) ||
    typeof author !== "string" ||
    typeof own !== "boolean" ||
    !Array.isArray(refutations)
  ) {
    throw new ApiError("Die Antwort zu einem Befund hat nicht die erwartete Form.");
  }

  return {
    ...toFinding(fields),
    origin,
    author,
    own,
    refutations: refutations.map(toRefutation),
  };
}

/**
 * Ein **Ausschnitt**, wie er in der Antwort zu den Befunden steht.
 *
 * Die Zeilen sind dieselben wie die des Diffs und werden mit demselben Leser
 * gelesen: Was im Diff eine Zeile Code ist, ist es hier auch — eine zweite
 * Zeilenform für dieselbe Sache wäre eine zweite Gelegenheit, sie verschieden
 * darzustellen.
 */
function toSiteExcerpt(fields: Record<string, unknown>): SiteExcerpt {
  const { path, side, source, truncated } = fields;
  const revisionNumber = fields["revision_number"];
  const startLine = fields["start_line"];
  const endLine = fields["end_line"];
  const lines = fields["lines"];

  if (
    typeof path !== "string" ||
    typeof revisionNumber !== "number" ||
    typeof startLine !== "number" ||
    typeof endLine !== "number" ||
    !(side === "old" || side === "new") ||
    !isExcerptSource(source) ||
    typeof truncated !== "boolean" ||
    !Array.isArray(lines)
  ) {
    throw new ApiError("Die Antwort zu einem Ausschnitt hat nicht die erwartete Form.");
  }

  return {
    path,
    revisionNumber,
    startLine,
    endLine,
    side,
    source,
    truncated,
    lines: lines.map((line) => toDiffLine(fieldsOf(line) ?? {})),
  };
}

/**
 * Der **Vermerkstand** eines Befundes, wie er neben den Befunden steht.
 *
 * Was hier nicht vorkommt, ist unvermerkt: Der Server schickt nur die Befunde
 * mit, an denen ein Vermerk hängt — eine Zeile je Befund wäre in einer Revision
 * mit dreißig Befunden dreißigmal die Auskunft „hier ist nichts".
 */
function toFollowUpTally(fields: Record<string, unknown>): FollowUpTally {
  const findingId = fields["finding_id"];
  const { total, mine } = fields;

  if (typeof findingId !== "number" || typeof total !== "number" || typeof mine !== "boolean") {
    throw new ApiError("Die Antwort zum Vermerkstand hat nicht die erwartete Form.");
  }

  return { findingId, total, mine };
}

/** Ein **Nachhalte-Vermerk** samt dem Befund, auf den er zeigt. */
function toFollowUpMark(fields: Record<string, unknown>): FollowUpMark {
  const { id, person, reason } = fields;
  const createdAt = fields["created_at"];
  const projectName = fields["project_name"];
  const revisionId = fields["revision_id"] ?? null;

  if (
    typeof id !== "number" ||
    typeof person !== "string" ||
    typeof reason !== "string" ||
    typeof createdAt !== "string" ||
    typeof projectName !== "string" ||
    !(typeof revisionId === "number" || revisionId === null)
  ) {
    throw new ApiError("Die Antwort zu einem Vermerk hat nicht die erwartete Form.");
  }

  return {
    id,
    person,
    reason,
    createdAt,
    projectName,
    revisionId,
    finding: toFinding(fieldOf(fields, "finding")),
  };
}

function toFollowUpMonth(fields: Record<string, unknown>): FollowUpMonth {
  const { month, marks } = fields;

  if (typeof month !== "string" || typeof marks !== "number") {
    throw new ApiError("Die Antwort zur Reihe der Monate hat nicht die erwartete Form.");
  }

  return { month, marks };
}

function toRefutation(entry: unknown): Refutation {
  const fields = fieldsOf(entry);
  const result = fields?.["result"];
  const author = fields?.["author"];
  const reason = fields?.["reason"];
  const createdAt = fields?.["created_at"] ?? null;

  if (
    !isRefutationResult(result) ||
    typeof author !== "string" ||
    typeof reason !== "string" ||
    !(typeof createdAt === "string" || createdAt === null)
  ) {
    throw new ApiError("Die Antwort zu einer Widerlegung hat nicht die erwartete Form.");
  }

  return { result, author, reason, createdAt };
}

function isFindingOrigin(value: unknown): value is FindingOrigin {
  return value === "agent" || value === "pattern" || value === "person";
}

function isRefutationResult(value: unknown): value is RefutationResult {
  return value === "upheld" || value === "refuted" || value === "unclear";
}

function toSubmission(fields: Record<string, unknown>): Submission {
  const { id } = fields;
  const revisionId = fields["revision_id"];
  const submittedAt = fields["submitted_at"];

  if (typeof id !== "number" || typeof revisionId !== "number" || typeof submittedAt !== "string") {
    throw new ApiError("Die Antwort zur Abgabe hat nicht die erwartete Form.");
  }

  return { id, revisionId, submittedAt };
}

function toComparisonAgent(fields: Record<string, unknown>): ComparisonAgent {
  const agentId = fields["agent_id"];
  const agentName = fields["agent_name"];
  const matches = fields["matches"];

  if (typeof agentId !== "number" || typeof agentName !== "string" || !Array.isArray(matches)) {
    throw new ApiError("Die Antwort zu einem Agenten der Gegenüberstellung hat nicht die erwartete Form.");
  }

  return {
    agentId,
    agentName,
    matches: matches.map((entry) => {
      const item = fieldsOf(entry);

      if (item === null) {
        throw new ApiError("Die Gegenüberstellung enthält etwas, das keine Zuordnung ist.");
      }

      return toFindingMatch(item);
    }),
  };
}

function toControlComparison(fields: Record<string, unknown>): ControlComparison {
  const matched = fields["matched"];
  const unmatched = fields["unmatched"];
  const findings = fields["findings"];
  const coverage = fields["coverage"] ?? null;

  if (
    typeof matched !== "number" ||
    typeof unmatched !== "number" ||
    typeof findings !== "number" ||
    !(typeof coverage === "number" || coverage === null)
  ) {
    throw new ApiError("Die Antwort zur dritten Spalte der Gegenüberstellung hat nicht die erwartete Form.");
  }

  return {
    matches: listOf(fields, "matches").map(toFindingMatch),
    matched,
    unmatched,
    findings,
    coverage,
  };
}

function toFindingMatch(fields: Record<string, unknown>): FindingMatch {
  const { id } = fields;
  const kindMatches = fields["kind_matches"];
  const severityMatches = fields["severity_matches"];

  if (
    typeof id !== "number" ||
    !(typeof kindMatches === "boolean" || kindMatches === null) ||
    !(typeof severityMatches === "boolean" || severityMatches === null)
  ) {
    throw new ApiError("Die Antwort zu einer Zuordnung hat nicht die erwartete Form.");
  }

  return {
    id,
    humanFinding: sideOf(fields, "human_finding"),
    machineFinding: sideOf(fields, "machine_finding"),
    kindMatches,
    severityMatches,
  };
}

/** Eine Seite einer Zuordnung. `null` heißt: Diese Seite hat nichts gesehen. */
function sideOf(fields: Record<string, unknown>, name: string): Finding | null {
  const value = fields[name] ?? null;

  if (value === null) {
    return null;
  }

  const finding = fieldsOf(value);

  if (finding === null) {
    throw new ApiError(`Die Zuordnung trägt unter „${name}" keinen Befund.`);
  }

  return toFinding(finding);
}

function toDeviation(fields: Record<string, unknown>): Deviation {
  const agentId = fields["agent_id"];
  const agentName = fields["agent_name"];
  const deviation = fields["deviation"];

  if (
    typeof agentId !== "number" ||
    typeof agentName !== "string" ||
    // JSON kennt nur `number`: Eine Quote von 1 kommt als ganze Zahl an.
    !(typeof deviation === "number" || deviation === null)
  ) {
    throw new ApiError("Die Antwort zur Abweichungskennzahl hat nicht die erwartete Form.");
  }

  return {
    agentId,
    agentName,
    matched: tallyOf(fields, "matched"),
    missed: tallyOf(fields, "missed"),
    unmatched: tallyOf(fields, "unmatched"),
    kindMatches: tallyOf(fields, "kind_matches"),
    severityMatches: tallyOf(fields, "severity_matches"),
    confirmed: tallyOf(fields, "confirmed"),
    rejected: tallyOf(fields, "rejected"),
    bothEmpty: tallyOf(fields, "both_empty"),
    findings: tallyOf(fields, "findings"),
    deviation,
  };
}

const COMPLETION_MALFORMED = "Die Antwort zur Abdeckung hat nicht die erwartete Form.";

function toCompletion(fields: Record<string, unknown>): Completion {
  const agents = fields["agents"];

  if (!Array.isArray(agents)) {
    throw new ApiError(COMPLETION_MALFORMED);
  }

  return {
    anyAgent: toCompletionWindows(completionObject(fields["any_agent"])),
    agents: agents.map((entry) => {
      const row = completionObject(entry);
      const agentId = row["agent_id"];
      const agentName = row["agent_name"];

      if (typeof agentId !== "number" || typeof agentName !== "string") {
        throw new ApiError(COMPLETION_MALFORMED);
      }

      return { agentId, agentName, ...toCompletionWindows(row) };
    }),
  };
}

function toCompletionWindows(fields: Record<string, unknown>): CompletionWindows {
  return {
    last24Hours: toCompletionTally(fields, "last_24_hours"),
    last7Days: toCompletionTally(fields, "last_7_days"),
    last30Days: toCompletionTally(fields, "last_30_days"),
    sinceStart: toCompletionTally(fields, "since_start"),
  };
}

/** Ein Fenster mit beiden Zahlen. Eine fehlende ist ein Fehler und keine Null. */
function toCompletionTally(fields: Record<string, unknown>, name: string): CompletionTally {
  const window = completionObject(fields[name]);
  const responsible = window["responsible"];
  const complete = window["complete"];

  if (typeof responsible !== "number" || typeof complete !== "number") {
    throw new ApiError(COMPLETION_MALFORMED);
  }

  return { responsible, complete };
}

/** Ein verschachteltes Objekt der Abdeckung. Etwas anderes ist ein Fehler. */
function completionObject(value: unknown): Record<string, unknown> {
  const fields = fieldsOf(value);

  if (fields === null) {
    throw new ApiError(COMPLETION_MALFORMED);
  }

  return fields;
}

/** Eine Zählung der Kennzahl. Eine fehlende ist ein Fehler und keine Null. */
function tallyOf(fields: Record<string, unknown>, name: string): number {
  const value = fields[name];

  if (typeof value !== "number") {
    throw new ApiError(`Die Abweichungskennzahl trägt keine Zählung „${name}".`);
  }

  return value;
}

function toModelCall(fields: Record<string, unknown>): ModelCall {
  const { id, request: sent } = fields;
  const agentId = fields["agent_id"];
  const agentName = fields["agent_name"];
  const endpointId = fields["endpoint_id"];
  const endpointName = fields["endpoint_name"];
  const presetIdentifier = fields["preset_identifier"];
  const resolvedBaseModel = fields["resolved_base_model"];
  const rawResponse = fields["raw_response"];
  const createdAt = fields["created_at"];

  if (
    typeof id !== "number" ||
    typeof agentId !== "number" ||
    typeof agentName !== "string" ||
    typeof endpointId !== "number" ||
    typeof endpointName !== "string" ||
    typeof presetIdentifier !== "string" ||
    typeof resolvedBaseModel !== "string" ||
    typeof sent !== "string" ||
    !(typeof rawResponse === "string" || rawResponse === null) ||
    typeof createdAt !== "string"
  ) {
    throw new ApiError("Die Antwort zu einem Modellaufruf hat nicht die erwartete Form.");
  }

  return {
    id,
    agentId,
    agentName,
    endpointId,
    endpointName,
    presetIdentifier,
    resolvedBaseModel,
    request: sent,
    rawResponse,
    durationMs: numberOrNull(fields, "duration_ms"),
    promptTokens: numberOrNull(fields, "prompt_tokens"),
    completionTokens: numberOrNull(fields, "completion_tokens"),
    createdAt,
  };
}

function toStalledJob(fields: Record<string, unknown>): StalledJob {
  const { id, kind, state, attempts, parked, reason } = fields;
  const agentId = fields["agent_id"];
  const agentName = fields["agent_name"];
  const path = fields["path"];

  if (
    typeof id !== "number" ||
    !(kind === "review" || kind === "synthesis") ||
    !isReviewJobState(state) ||
    typeof attempts !== "number" ||
    typeof agentId !== "number" ||
    typeof agentName !== "string" ||
    typeof parked !== "boolean" ||
    !(typeof reason === "string" || reason === null) ||
    !(typeof path === "string" || path === null)
  ) {
    throw new ApiError("Die Antwort zu einem Prüfauftrag hat nicht die erwartete Form.");
  }

  return {
    id,
    kind,
    state,
    attempts,
    agentId,
    agentName,
    parked,
    reason,
    revisionNumber: numberOrNull(fields, "revision_number"),
    path,
  };
}

/**
 * Die Auskunft eines Sammelvorgangs (#94).
 *
 * Beide Zahlen müssen dastehen: Fehlte die übersprungene, sagte die Oberfläche
 * „alles eingereiht" über etwas, das liegen geblieben ist.
 */
function toRequeueTally(body: unknown): RequeueTally {
  const fields = fieldsOf(body);
  const requeued = fields === null ? null : fields["requeued"];
  const skippedParked = fields === null ? null : fields["skipped_parked"];

  if (typeof requeued !== "number" || typeof skippedParked !== "number") {
    throw new ApiError(
      "Die Antwort zum erneuten Einreihen nennt nicht, wie viele Prüfaufträge eingereiht wurden.",
    );
  }

  return { requeued, skippedParked };
}

function toQueueLoad(fields: Record<string, unknown>): QueueLoad {
  const { parked } = fields;
  const endpointName = fields["endpoint_name"];
  const baseModel = fields["base_model"];

  if (
    !(typeof endpointName === "string" || endpointName === null) ||
    !(typeof baseModel === "string" || baseModel === null) ||
    typeof parked !== "number"
  ) {
    throw new ApiError("Die Antwort zum Warteschlangenstand hat nicht die erwartete Form.");
  }

  return {
    endpointId: numberOrNull(fields, "endpoint_id"),
    endpointName,
    baseModel,
    // Kein Slot ist etwas anderes als null Slots: Die Kontrolle braucht keinen
    // (ADR-0010), und eine Null behauptete, ihr fehle Kapazität.
    slots: numberOrNull(fields, "slots"),
    pending: toKindCounts(fields["pending"]),
    claimed: toKindCounts(fields["claimed"]),
    parked,
  };
}

/**
 * Eine Abbildung Art → Anzahl, mit **allen vier Arten als Zahl**.
 *
 * Eine fehlende Art ist hier ein Formfehler und keine Null: Der Server sagt zu,
 * jede Art zu nennen, und eine hier erfundene Null verdeckte, dass er es nicht
 * getan hat.
 */
function toKindCounts(value: unknown): Record<ReviewJobKind, number> {
  const counts = fieldsOf(value) ?? {};

  return {
    review: amountOf(counts, "review"),
    synthesis: amountOf(counts, "synthesis"),
    control: amountOf(counts, "control"),
    refutation: amountOf(counts, "refutation"),
  };
}

function amountOf(counts: Record<string, unknown>, kind: ReviewJobKind): number {
  const value = counts[kind];

  if (typeof value !== "number") {
    throw new ApiError(`Der Warteschlangenstand zählt „${kind}" nicht.`);
  }

  return value;
}

/** Eine Zahl, die auch fehlen darf — Dauer, Token, Revisionsnummer einer Synthese. */
function numberOrNull(fields: Record<string, unknown>, name: string): number | null {
  const value = fields[name] ?? null;

  if (!(typeof value === "number" || value === null)) {
    throw new ApiError(`Das Feld „${name}" ist keine Zahl.`);
  }

  return value;
}

function toProjectOption(fields: Record<string, unknown>): ProjectOption {
  const { id, name } = fields;

  if (typeof id !== "number" || typeof name !== "string") {
    throw new ApiError("Die Antwort zu einem Projekt des Stroms hat nicht die erwartete Form.");
  }

  return { id, name };
}

/**
 * Die Einordnung einer Seite: Seitenzahl, Seitengröße, Zahl der Seiten und die
 * Gesamtzahl. Sie steht an jeder seitenweisen Antwort und wird deshalb an einer
 * Stelle gelesen.
 */
function pageOf(
  body: unknown,
  what: string,
): { page: number; perPage: number; pages: number; total: number } {
  const fields = fieldsOf(body);
  const page = fields === null ? null : fields["page"];
  const perPage = fields === null ? null : fields["per_page"];
  const pages = fields === null ? null : fields["pages"];
  const total = fields === null ? null : fields["total"];

  if (
    typeof page !== "number" ||
    typeof perPage !== "number" ||
    typeof pages !== "number" ||
    typeof total !== "number"
  ) {
    throw new ApiError(`Die Antwort zu ${what} hat nicht die erwartete Form.`);
  }

  return { page, perPage, pages, total };
}

function toInventoryFile(fields: Record<string, unknown>): InventoryFile {
  const { path, total } = fields;
  const revisionNumber = fields["revision_number"];
  const severities = fieldsOf(fields["severities"]);

  if (
    typeof path !== "string" ||
    typeof revisionNumber !== "number" ||
    typeof total !== "number" ||
    severities === null
  ) {
    throw new ApiError("Die Antwort zu einer Datei des Bestands hat nicht die erwartete Form.");
  }

  return {
    path,
    revisionNumber,
    total,
    severities: {
      hinweis: countOf(severities, "hinweis"),
      mittel: countOf(severities, "mittel"),
      schwer: countOf(severities, "schwer"),
    },
  };
}

/** Eine Zählung je Schweregrad. Was fehlt, ist keine Null, sondern eine andere Antwort. */
function countOf(severities: Record<string, unknown>, severity: Severity): number {
  const value = severities[severity];

  if (typeof value !== "number") {
    throw new ApiError(`Die Datei des Bestands zählt „${severity}" nicht.`);
  }

  return value;
}

function toControlPatternOption(fields: Record<string, unknown>): ControlPatternOption {
  const { identifier, label } = fields;

  if (typeof identifier !== "string" || typeof label !== "string") {
    throw new ApiError("Die Antwort zu einem Kontrollmuster hat nicht die erwartete Form.");
  }

  return { identifier, label };
}

function toProjectFile(fields: Record<string, unknown>): ProjectFile {
  const { path, content } = fields;
  const revisionNumber = fields["revision_number"];

  if (
    typeof path !== "string" ||
    typeof revisionNumber !== "number" ||
    typeof content !== "string"
  ) {
    throw new ApiError("Die Antwort zu einer Datei hat nicht die erwartete Form.");
  }

  return { path, revisionNumber, content };
}

/**
 * Eine benannte Liste von Zeichenketten — die SVN-Kennungen eines Filters, die
 * Werte einer geschlossenen Menge.
 */
function namesOf(body: unknown, name: string): string[] {
  const fields = fieldsOf(body);
  const value = fields === null ? null : fields[name];

  if (!Array.isArray(value)) {
    throw new ApiError(`Die Antwort trägt keine Liste „${name}".`);
  }

  return value.map((entry) => {
    if (typeof entry !== "string") {
      throw new ApiError(`Die Liste „${name}" enthält etwas, das keine Zeichenkette ist.`);
    }

    return entry;
  });
}

function toEndpoint(fields: Record<string, unknown>): Endpoint {
  const { id, name, address } = fields;
  const maxParallelCalls = fields["max_parallel_calls"];

  if (
    typeof id !== "number" ||
    typeof name !== "string" ||
    typeof address !== "string" ||
    typeof maxParallelCalls !== "number"
  ) {
    throw new ApiError("Die Antwort zu einem Endpunkt hat nicht die erwartete Form.");
  }

  return { id, name, address, maxParallelCalls };
}

function toPreset(fields: Record<string, unknown>): Preset {
  const identifier = fields["identifier"];
  const baseModel = fields["base_model"];

  if (typeof identifier !== "string" || typeof baseModel !== "string") {
    throw new ApiError("Die Antwort zu einem Preset hat nicht die erwartete Form.");
  }

  return { identifier, baseModel };
}

function toBaseModel(fields: Record<string, unknown>): BaseModel {
  const { id, identifier, slots } = fields;
  const endpointId = fields["endpoint_id"];
  const contextWindow = fields["context_window"];

  if (
    typeof id !== "number" ||
    typeof endpointId !== "number" ||
    typeof identifier !== "string" ||
    typeof slots !== "number" ||
    !(typeof contextWindow === "number" || contextWindow === null)
  ) {
    throw new ApiError("Die Antwort zu einem Basismodell hat nicht die erwartete Form.");
  }

  return { id, endpointId, identifier, slots, contextWindow };
}

function toAgent(fields: Record<string, unknown>): Agent {
  const { id, name, state } = fields;
  const endpointId = fields["endpoint_id"];
  const presetIdentifier = fields["preset_identifier"];
  const contextLimit = fields["context_limit"];
  const reviewPromptTemplate = fields["review_prompt_template"];
  const synthesisPromptTemplate = fields["synthesis_prompt_template"];
  const lastSeenBaseModel = fields["last_seen_base_model"];
  const parkedReason = fields["parked_reason"];
  const activatedAt = fields["activated_at"];
  const refutationEnabled = fields["refutation_enabled"];
  const synthesisAgent = fields["synthesis_agent"];
  const reasoning = fields["reasoning"];

  if (
    typeof id !== "number" ||
    typeof name !== "string" ||
    typeof endpointId !== "number" ||
    typeof presetIdentifier !== "string" ||
    typeof contextLimit !== "number" ||
    typeof reviewPromptTemplate !== "string" ||
    typeof synthesisPromptTemplate !== "string" ||
    !isAgentState(state) ||
    !(typeof lastSeenBaseModel === "string" || lastSeenBaseModel === null) ||
    !(typeof parkedReason === "string" || parkedReason === null) ||
    !(typeof activatedAt === "string" || activatedAt === null) ||
    typeof refutationEnabled !== "boolean" ||
    typeof synthesisAgent !== "boolean" ||
    typeof reasoning !== "boolean"
  ) {
    throw new ApiError("Die Antwort zu einem Agenten hat nicht die erwartete Form.");
  }

  return {
    id,
    name,
    endpointId,
    presetIdentifier,
    parameters: toParameters(fields["parameters"]),
    contextLimit,
    reviewPromptTemplate,
    synthesisPromptTemplate,
    lastSeenBaseModel,
    state,
    parkedReason,
    activatedAt,
    refutationEnabled,
    synthesisAgent,
    reasoning,
  };
}

/**
 * Die Parameter eines Agenten. Welche ein Endpunkt versteht, weiß er selbst —
 * geprüft wird deshalb nur, dass es benannte einfache Werte sind.
 */
function toParameters(value: unknown): AgentParameters {
  const fields = fieldsOf(value);

  if (fields === null) {
    throw new ApiError("Die Parameter eines Agenten haben nicht die erwartete Form.");
  }

  const parameters: AgentParameters = {};

  for (const [name, entry] of Object.entries(fields)) {
    if (typeof entry !== "string" && typeof entry !== "number" && typeof entry !== "boolean") {
      throw new ApiError(`Der Parameter „${name}" ist kein einfacher Wert.`);
    }

    parameters[name] = entry;
  }

  return parameters;
}

function toProject(fields: Record<string, unknown>): Project {
  const { id, name, active } = fields;
  const svnPath = fields["svn_path"];
  const currentPhpVersion = fields["current_php_version"];
  const plannedPhpVersions = fields["planned_php_versions"];
  const startRevision = fields["start_revision"];
  const lastProcessedRevision = fields["last_processed_revision"];
  const lastPollAt = fields["last_poll_at"];
  const lastPollResult = fields["last_poll_result"];
  const displayThreshold = fields["display_threshold"];

  if (
    !isSeverity(displayThreshold) ||
    typeof id !== "number" ||
    typeof name !== "string" ||
    typeof svnPath !== "string" ||
    typeof currentPhpVersion !== "string" ||
    !Array.isArray(plannedPhpVersions) ||
    !plannedPhpVersions.every((entry) => typeof entry === "string") ||
    typeof startRevision !== "number" ||
    typeof lastProcessedRevision !== "number" ||
    typeof active !== "boolean" ||
    !(typeof lastPollAt === "string" || lastPollAt === null) ||
    !(typeof lastPollResult === "string" || lastPollResult === null)
  ) {
    throw new ApiError("Die Antwort zu einem Projekt hat nicht die erwartete Form.");
  }

  return {
    id,
    name,
    svnPath,
    currentPhpVersion,
    plannedPhpVersions,
    startRevision,
    lastProcessedRevision,
    active,
    lastPollAt,
    lastPollResult,
    displayThreshold,
  };
}

function sentIssueReferenceRule(draft: IssueReferenceRuleDraft): Record<string, unknown> {
  return {
    expression: draft.expression,
    target: draft.target,
    // Eine Stelle, die niemand genannt hat, wird auch nicht mitgeschickt: Beim
    // Anlegen steht die Regel dann hinten, beim Ändern bleibt sie, wo sie war.
    ...(draft.position === undefined ? {} : { position: draft.position }),
    // Das Kürzel geht dagegen **auch leer** mit, sobald der Dialog es führt: Leer
    // ist dort die Auskunft, dass die Ticketliste keines haben soll (#131).
    ...(draft.prefix === undefined ? {} : { prefix: draft.prefix }),
  };
}

function toIssueReferenceRule(fields: Record<string, unknown>): IssueReferenceRule {
  const { id, expression, target, position } = fields;
  const complaint = fields["complaint"] ?? null;
  const prefix = fields["prefix"] ?? null;

  if (
    typeof id !== "number" ||
    typeof expression !== "string" ||
    typeof target !== "string" ||
    typeof position !== "number" ||
    !(typeof prefix === "string" || prefix === null) ||
    !(typeof complaint === "string" || complaint === null)
  ) {
    throw new ApiError("Die Antwort zu einer Zuordnungsregel hat nicht die erwartete Form.");
  }

  return { id, expression, target, position, prefix, complaint };
}

function toControlPattern(fields: Record<string, unknown>): ControlPattern {
  const { id, identifier, detection, label, message, kind, severity, active } = fields;
  const sniffCode = fields["sniff_code"] ?? null;
  const fragment = fields["fragment"] ?? null;
  const positiveExample = fields["positive_example"] ?? null;
  const negativeExample = fields["negative_example"] ?? null;
  const retiredAt = fields["retired_at"] ?? null;
  const retiredByName = fields["retired_by_name"] ?? null;
  const retiredReason = fields["retired_reason"] ?? null;
  const findingCount = fields["finding_count"];
  const inventoried = fields["inventoried"];

  if (
    typeof findingCount !== "number" ||
    typeof inventoried !== "boolean" ||
    typeof id !== "number" ||
    typeof identifier !== "string" ||
    typeof detection !== "string" ||
    !(typeof sniffCode === "string" || sniffCode === null) ||
    !(typeof fragment === "string" || fragment === null) ||
    !(typeof positiveExample === "string" || positiveExample === null) ||
    !(typeof negativeExample === "string" || negativeExample === null) ||
    typeof label !== "string" ||
    typeof message !== "string" ||
    typeof kind !== "string" ||
    typeof severity !== "string" ||
    typeof active !== "boolean" ||
    !(typeof retiredAt === "string" || retiredAt === null) ||
    !(typeof retiredByName === "string" || retiredByName === null) ||
    !(typeof retiredReason === "string" || retiredReason === null)
  ) {
    throw new ApiError("Die Antwort zu einem Kontrollmuster hat nicht die erwartete Form.");
  }

  return {
    id,
    identifier,
    detection,
    sniffCode,
    fragment,
    positiveExample,
    negativeExample,
    label,
    message,
    kind,
    severity,
    active,
    retiredAt,
    retiredByName,
    retiredReason,
    originFinding: toFindingAnchor(fields["origin_finding"]),
    findingCount,
    inventoried,
  };
}

/**
 * Der Anker eines Befundes, wo einer dasteht — `null` heißt: keiner.
 *
 * **Halb gibt es ihn nicht.** Ein Anker ohne Revision oder ohne Zeilen
 * bezeichnete nichts (ADR-0006), und ein Verweis, dem niemand folgen kann, ist
 * schlechter als keiner.
 */
function toFindingAnchor(value: unknown): FindingAnchor | null {
  const fields = fieldsOf(value);

  if (fields === null) {
    return null;
  }

  const { id, path } = fields;
  const projectId = fields["project_id"];
  const revisionNumber = fields["revision_number"];
  const startLine = fields["start_line"];
  const endLine = fields["end_line"];

  if (
    typeof id !== "number" ||
    typeof projectId !== "number" ||
    typeof path !== "string" ||
    typeof revisionNumber !== "number" ||
    typeof startLine !== "number" ||
    typeof endLine !== "number"
  ) {
    return null;
  }

  return { id, projectId, path, revisionNumber, startLine, endLine };
}

function toInventoryRun(fields: Record<string, unknown>): InventoryRun {
  const { id, patterns, files } = fields;
  const projectId = fields["project_id"];
  const revisionNumber = fields["revision_number"];

  if (
    typeof id !== "number" ||
    typeof projectId !== "number" ||
    typeof revisionNumber !== "number" ||
    typeof files !== "number" ||
    !Array.isArray(patterns) ||
    !patterns.every((entry) => typeof entry === "string")
  ) {
    throw new ApiError("Die Antwort zu einem Bestandslauf hat nicht die erwartete Form.");
  }

  return { id, projectId, revisionNumber, patterns, files };
}

function toPerson(fields: Record<string, unknown>): Person {
  const { id, role, active } = fields;
  const displayName = fields["display_name"];
  const erpIdentity = fields["erp_identity"];
  const blindReview = fields["blind_review"];
  const chatHandle = fields["chat_handle"];
  const svnIdentities = fields["svn_identities"];

  if (
    typeof id !== "number" ||
    typeof displayName !== "string" ||
    typeof erpIdentity !== "string" ||
    typeof active !== "boolean" ||
    typeof blindReview !== "boolean" ||
    !(typeof chatHandle === "string" || chatHandle === null) ||
    !Array.isArray(svnIdentities) ||
    !svnIdentities.every((entry) => typeof entry === "string") ||
    !isRole(role)
  ) {
    throw new ApiError("Die Antwort zu einer Person hat nicht die erwartete Form.");
  }

  return { id, displayName, erpIdentity, role, active, blindReview, chatHandle, svnIdentities };
}

function toSvnIdentity(fields: Record<string, unknown>): SvnIdentity {
  const { id, identity } = fields;
  const personId = fields["person_id"];

  if (
    typeof id !== "number" ||
    typeof identity !== "string" ||
    !(typeof personId === "number" || personId === null)
  ) {
    throw new ApiError("Die Antwort zu einer SVN-Kennung hat nicht die erwartete Form.");
  }

  return { id, identity, personId };
}

function isAgentState(value: unknown): value is AgentState {
  return value === "active" || value === "retired" || value === "parked";
}

function isChangeAction(value: unknown): value is ChangeAction {
  return value === "added" || value === "modified" || value === "deleted" || value === "moved";
}

/** Die fünf Werte des Glossars. Ein sechster ist eine unerwartete Form, kein Ersatzwert. */
function isFindingKind(value: unknown): value is FindingKind {
  return (
    value === "syntax" ||
    value === "konvention" ||
    value === "nicht-produktiv" ||
    value === "struktur" ||
    value === "migration"
  );
}

function isSeverity(value: unknown): value is Severity {
  return value === "hinweis" || value === "mittel" || value === "schwer";
}

function isFindingStatus(value: unknown): value is FindingStatus {
  return (
    value === "open" || value === "fixed" || value === "dismissed" || value === "superseded"
  );
}

function isQualification(value: unknown): value is Qualification {
  return value === "confirmed" || value === "rejected" || value === "acknowledged";
}

function isReviewJobState(value: unknown): value is ReviewJobState {
  return value === "pending" || value === "claimed" || value === "done" || value === "failed";
}

function isDiffLineKind(value: unknown): value is DiffLineKind {
  return value === "context" || value === "added" || value === "removed";
}

function isExcerptSource(value: unknown): value is ExcerptSource {
  return value === "content" || value === "diff" || value === "none";
}

function isRevisionState(value: unknown): value is RevisionState {
  return (
    value === "pending" || value === "in_review" || value === "reviewed" || value === "failed"
  );
}

function isRole(value: unknown): value is Role {
  return value === "dev" || value === "management" || value === "admin";
}

function fieldsOf(body: unknown): Record<string, unknown> | null {
  return typeof body === "object" && body !== null ? (body as Record<string, unknown>) : null;
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
